/**
 * Base Model Constants V2 - Ecosystem-based Schema
 *
 * This file implements the redesigned schema with:
 * - Hierarchical ecosystems (parent/child relationships)
 * - Separated concerns: identity, support, settings
 * - Inheritance-based resolution
 * - Minimal explicit rules (derive support from ecosystem relationships)
 *
 * @see docs/generation-support-redesign.md for design documentation
 */

import { ModelType, type MediaType } from '@civitai/db-schema/enums';
import { lazy } from './lazy';
import { isDefined } from './type-guards';

// =============================================================================
// Types
// =============================================================================

export type SupportLevel = 'full' | 'partial';
export type SupportType = 'generation' | 'training' | 'auction';

// Generation modes supported by ecosystems
export type GenerationMode = 'txt2img' | 'img2img' | 'txt2vid' | 'img2vid' | 'vid2vid';

// -----------------------------------------------------------------------------
// Ecosystem Types
// -----------------------------------------------------------------------------

export type EcosystemRecord = {
  id: number;
  key: string; // Stable identifier for data mapping (e.g., 'SDXL', 'Flux1')
  displayName: string; // Human/SEO friendly display name (e.g., 'Stable Diffusion XL')
  description?: string; // Brief description for UI display
  parentEcosystemId?: number;
  familyId?: number; // For UI family grouping
  sortOrder: number; // For ordering in UI
  /**
   * True when this ecosystem's generation routes to Civitai-hosted GPUs/workers
   * (vs external providers like FAL/Google/OpenAI). Drives the self-hosted
   * generation toggle. Stamped from `SELF_HOSTED_ECOSYSTEM_KEYS` below.
   */
  selfHosted?: boolean;
};

export type EcosystemSupport = {
  ecosystemId: number;
  supportType: SupportType;
  modelTypes: ModelType[];
  disabled?: boolean; // Defaults to false (enabled)
};

export type EcosystemSettings = {
  ecosystemId: number;
  defaults?: {
    model?: { id: number };
    /** If true, the model cannot be changed by the user (used for video ecosystems) */
    modelLocked?: boolean;
    /** Orchestrator engine identifier. Used for backwards compatibility with getBaseModelEngine(). */
    engine?: string;
  };
};

export type CrossEcosystemRule = {
  sourceEcosystemId: number;
  targetEcosystemId: number;
  supportType: SupportType;
  modelTypes?: ModelType[];
  support: 'partial';
};

export type SupportOverride = {
  ecosystemId?: number;
  baseModelId?: number;
  supportType: SupportType;
  modelTypes?: ModelType[];
  disabled?: boolean; // Defaults to false (enabled)
};

// -----------------------------------------------------------------------------
// Other Types (unchanged from v1)
// -----------------------------------------------------------------------------

export type LicenseRecord = {
  id: number;
  name: string;
  url?: string;
  notice?: string;
  poweredBy?: string;
  // Set only when the licence obliges us to name the model in the product's own
  // UI, and holds the exact string it demands. Distinct from `poweredBy`, which
  // several licences use for a liability disclaimer rather than an attribution.
  attribution?: string;
  disableMature?: boolean;
  // License forbids commercial use (drives commercial-use override + monetization block).
  nonCommercial?: boolean;
};

export type BaseModelFamilyRecord = {
  id: number;
  name: string;
  description: string;
};

export type BaseModelRecord = {
  id: number;
  name: string;
  description?: string;
  type: MediaType | MediaType[];
  ecosystemId: number; // Direct reference to ecosystem
  hidden?: boolean;
  disabled?: boolean; // Disables ALL support types (generation, training, auction, etc.)
  licenseId?: number;
  experimental?: boolean; // If true, show experimental warning in generation UI
};

// =============================================================================
// Ecosystem Constants
// =============================================================================

export const ECO = {
  // Root ecosystems - Image models
  SD1: 1,
  SD2: 2,
  SD3: 3,
  SD35M: 46, // SD 3.5 Medium - separate ecosystem
  SDXL: 4,
  SDXLDistilled: 5,
  Flux1: 6,
  FluxKrea: 7,
  Flux1Kontext: 8,
  Flux2: 9,
  Flux2Klein_9B: 54,
  Flux2Klein_9B_base: 55,
  Flux2Klein_4B: 56,
  Flux2Klein_4B_base: 57,
  Qwen: 10,
  Qwen2: 62,
  Qwen3: 80,
  Chroma: 11,
  HyDit1: 12,
  AuraFlow: 13,
  HiDream: 14,
  HiDreamO1: 29,
  Kolors: 15,
  Lumina: 16,
  Mochi: 17,
  PixArtA: 18,
  PixArtE: 19,
  NanoBanana: 20,
  OpenAI: 21,
  Imagen4: 22,
  Seedream: 23,
  ZImageTurbo: 24,
  ZImageBase: 53,
  SCascade: 25,
  PlaygroundV2: 26,
  ODOR: 27,
  Other: 28,
  Lens: 69,

  // Root ecosystems - Video models
  HyV1: 30,
  WanVideo: 31,
  WanVideo1_3B_T2V: 32,
  WanVideo14B_T2V: 33,
  WanVideo14B_I2V_480p: 34,
  WanVideo14B_I2V_720p: 35,
  WanVideo22_TI2V_5B: 36,
  WanVideo22_I2V_A14B: 37,
  WanVideo22_T2V_A14B: 38,
  WanVideo25_T2V: 39,
  WanVideo25_I2V: 40,
  WanImage27: 64,
  WanVideo27: 65,
  WanVideo30: 82,
  CogVideoX: 41,
  LTXV: 42,
  LTXV2: 58,
  LTXV23: 63,
  // 2.5 ships its own 22B weights, so a 2.3 LoRA is not guaranteed to load on
  // it — separate ecosystem, bridgeable later with a crossEcosystemRule.
  LTXV25: 81,
  Sora2: 43,
  Veo3: 44,
  SVD: 45,
  Vidu: 47,
  MiniMaxH3: 48,
  Kling: 49,
  Haiper: 50,
  Lightricks: 51,
  Seedance: 60,
  Anima: 59,
  Grok: 61,
  HappyHorse: 52,
  // FLUX-3 ships as separate weight releases per modality (Video now, Image and
  // the open-weight Dev backbone later) off a shared multimodal architecture.
  // Shared architecture is not shared weights, so each gets its own ecosystem —
  // a LoRA is trained against weights. Same reasoning as the Flux2Klein variants.
  Flux3Video: 79,

  // Root ecosystems - Audio models
  AceAudio: 68,

  // Root ecosystems - 3D Model providers
  // PolyGen has been displaced twice on main merges:
  //   originally 71 on the hackaton branch; bumped to 72 to dodge MAI
  //   (main-line); bumped again to 73 to dodge Ideogram (main-line, also
  //   landed on 72). Same renumber-cascade as BM.PolyGen (see BM block).
  PolyGen: 73,
  Tripo: 75,
  Hunyuan3D: 76,

  // Utility ecosystems
  Upscaler: 66,

  // Baidu
  Ernie: 67,

  // Krea AI
  Krea2: 70,

  // Microsoft
  MAI: 71,
  MageFlow: 78,

  // Ideogram
  Ideogram: 72,

  // Boogu
  Boogu: 74,

  // Reve AI
  Reve: 77,

  // Child ecosystems of SDXL
  Pony: 100,
  Illustrious: 101,
  NoobAI: 102,

  // Child ecosystems of AuraFlow
  PonyV7: 200,
} as const;

// Guard against duplicate ids — `ecosystemById` is keyed by id, so collisions
// silently overwrite earlier entries and cause cross-ecosystem mis-routing.
(() => {
  const seen = new Map<number, string>();
  for (const [k, v] of Object.entries(ECO)) {
    const prior = seen.get(v);
    if (prior !== undefined) throw new Error(`Duplicate ECO id ${v}: ${prior} and ${k}`);
    seen.set(v, k);
  }
})();

// =============================================================================
// Ecosystems
// =============================================================================

export const ecosystems: EcosystemRecord[] = [
  // Flux Family (familyId: 1)
  {
    id: ECO.Flux1,
    key: 'Flux1',
    displayName: 'Flux.1',
    familyId: 1,
    sortOrder: 0,
  },
  {
    id: ECO.FluxKrea,
    key: 'FluxKrea',
    displayName: 'Flux.1 Krea',
    familyId: 1,
    sortOrder: 1,
  },
  {
    id: ECO.Flux1Kontext,
    key: 'Flux1Kontext',
    displayName: 'Flux.1 Kontext',
    familyId: 1,
    sortOrder: 2,
  },
  {
    id: ECO.Flux2,
    key: 'Flux2',
    displayName: 'Flux.2',
    familyId: 1,
    sortOrder: 3,
  },
  {
    id: ECO.Flux2Klein_9B,
    key: 'Flux2Klein_9B',
    displayName: 'Flux.2 Klein 9B',
    familyId: 1,
    sortOrder: 4,
    parentEcosystemId: ECO.Flux2,
  },
  {
    id: ECO.Flux2Klein_9B_base,
    key: 'Flux2Klein_9B_base',
    displayName: 'Flux.2 Klein 9B Base',
    familyId: 1,
    sortOrder: 5,
    parentEcosystemId: ECO.Flux2,
  },
  {
    id: ECO.Flux2Klein_4B,
    key: 'Flux2Klein_4B',
    displayName: 'Flux.2 Klein 4B',
    familyId: 1,
    sortOrder: 6,
    parentEcosystemId: ECO.Flux2,
  },
  {
    id: ECO.Flux2Klein_4B_base,
    key: 'Flux2Klein_4B_base',
    displayName: 'Flux.2 Klein 4B Base',
    familyId: 1,
    sortOrder: 7,
    parentEcosystemId: ECO.Flux2,
  },
  {
    id: ECO.Flux3Video,
    key: 'Flux3Video',
    displayName: 'Flux 3 Video',
    familyId: 1,
    sortOrder: 8,
  },

  // Stable Diffusion Family (familyId: 2)
  {
    id: ECO.SD1,
    key: 'SD1',
    displayName: 'Stable Diffusion 1.x',
    familyId: 2,
    sortOrder: 10,
  },
  {
    id: ECO.SD2,
    key: 'SD2',
    displayName: 'Stable Diffusion 2.x',
    familyId: 2,
    sortOrder: 11,
  },
  {
    id: ECO.SD3,
    key: 'SD3',
    displayName: 'Stable Diffusion 3',
    familyId: 2,
    sortOrder: 12,
  },
  {
    id: ECO.SD35M,
    key: 'SD3_5M',
    displayName: 'Stable Diffusion 3.5 Medium',
    familyId: 2,
    sortOrder: 13,
  },
  {
    id: ECO.SDXL,
    key: 'SDXL',
    displayName: 'Stable Diffusion XL',
    familyId: 2,
    sortOrder: 14,
  },
  {
    id: ECO.SDXLDistilled,
    key: 'SDXLDistilled',
    displayName: 'SDXL Distilled',
    familyId: 2,
    sortOrder: 15,
  },
  {
    id: ECO.SCascade,
    key: 'SCascade',
    displayName: 'Stable Cascade',
    familyId: 2,
    sortOrder: 16,
  },
  {
    id: ECO.SVD,
    key: 'SVD',
    displayName: 'Stable Video Diffusion',
    familyId: 2,
    sortOrder: 17,
  },

  // SDXL Community Family (familyId: 3)
  {
    id: ECO.Illustrious,
    key: 'Illustrious',
    displayName: 'Illustrious',
    parentEcosystemId: ECO.SDXL,
    familyId: 3,
    sortOrder: 20,
  },
  {
    id: ECO.NoobAI,
    key: 'NoobAI',
    displayName: 'NoobAI',
    parentEcosystemId: ECO.SDXL,
    familyId: 3,
    sortOrder: 21,
  },

  // Hunyuan Family (familyId: 4)
  {
    id: ECO.HyDit1,
    key: 'HyDit1',
    displayName: 'Hunyuan DiT',
    familyId: 4,
    sortOrder: 40,
  },
  {
    id: ECO.HyV1,
    key: 'HyV1',
    displayName: 'Hunyuan Video',
    familyId: 4,
    sortOrder: 41,
  },

  // Wan Video Family (familyId: 5)
  {
    id: ECO.WanVideo,
    key: 'WanVideo',
    displayName: 'Wan Video',
    familyId: 5,
    sortOrder: 50,
  },
  {
    id: ECO.WanVideo1_3B_T2V,
    key: 'WanVideo1_3B_T2V',
    displayName: 'Wan Video 1.3B T2V',
    familyId: 5,
    sortOrder: 51,
  },
  {
    id: ECO.WanVideo14B_T2V,
    key: 'WanVideo14B_T2V',
    displayName: 'Wan Video 14B T2V',
    familyId: 5,
    sortOrder: 52,
  },
  {
    id: ECO.WanVideo14B_I2V_480p,
    key: 'WanVideo14B_I2V_480p',
    displayName: 'Wan Video 14B I2V 480p',
    familyId: 5,
    sortOrder: 53,
  },
  {
    id: ECO.WanVideo14B_I2V_720p,
    key: 'WanVideo14B_I2V_720p',
    displayName: 'Wan Video 14B I2V 720p',
    familyId: 5,
    sortOrder: 54,
  },
  {
    id: ECO.WanVideo22_TI2V_5B,
    key: 'WanVideo-22-TI2V-5B',
    displayName: 'Wan Video 2.2 TI2V 5B',
    familyId: 5,
    sortOrder: 55,
  },
  {
    id: ECO.WanVideo22_I2V_A14B,
    key: 'WanVideo-22-I2V-A14B',
    displayName: 'Wan Video 2.2 I2V A14B',
    familyId: 5,
    sortOrder: 56,
  },
  {
    id: ECO.WanVideo22_T2V_A14B,
    key: 'WanVideo-22-T2V-A14B',
    displayName: 'Wan Video 2.2 T2V A14B',
    familyId: 5,
    sortOrder: 57,
  },
  {
    id: ECO.WanVideo25_T2V,
    key: 'WanVideo-25-T2V',
    displayName: 'Wan Video 2.5 T2V',
    familyId: 5,
    sortOrder: 58,
  },
  {
    id: ECO.WanVideo25_I2V,
    key: 'WanVideo-25-I2V',
    displayName: 'Wan Video 2.5 I2V',
    familyId: 5,
    sortOrder: 59,
  },
  {
    id: ECO.WanImage27,
    key: 'WanImage27',
    displayName: 'Wan Image 2.7',
    familyId: 5,
    sortOrder: 60,
  },
  {
    id: ECO.WanVideo27,
    key: 'WanVideo27',
    displayName: 'Wan Video 2.7',
    familyId: 5,
    sortOrder: 61,
  },
  {
    id: ECO.WanVideo30,
    key: 'WanVideo30',
    displayName: 'Wan Video 3.0',
    familyId: 5,
    sortOrder: 62,
  },

  // PixArt Family (familyId: 6)
  {
    id: ECO.PixArtA,
    key: 'PixArtA',
    displayName: 'PixArt Alpha',
    familyId: 6,
    sortOrder: 60,
  },
  {
    id: ECO.PixArtE,
    key: 'PixArtE',
    displayName: 'PixArt Sigma',
    familyId: 6,
    sortOrder: 61,
  },

  // Google Family (familyId: 7)
  {
    id: ECO.Imagen4,
    key: 'Imagen4',
    displayName: 'Imagen 4',
    familyId: 7,
    sortOrder: 70,
  },
  {
    id: ECO.NanoBanana,
    key: 'NanoBanana',
    displayName: 'Nano Banana',
    familyId: 7,
    sortOrder: 71,
  },
  {
    id: ECO.Veo3,
    key: 'Veo3',
    displayName: 'Veo 3',
    familyId: 7,
    sortOrder: 72,
  },

  // OpenAI Family (familyId: 8)
  {
    id: ECO.OpenAI,
    key: 'OpenAI',
    displayName: 'OpenAI',
    familyId: 8,
    sortOrder: 80,
  },
  {
    id: ECO.Sora2,
    key: 'Sora2',
    displayName: 'Sora 2',
    familyId: 8,
    sortOrder: 81,
  },

  // Pony Diffusion Family (familyId: 9)
  {
    id: ECO.Pony,
    key: 'Pony',
    displayName: 'Pony Diffusion',
    parentEcosystemId: ECO.SDXL,
    familyId: 9,
    sortOrder: 30,
  },
  {
    id: ECO.PonyV7,
    key: 'PonyV7',
    displayName: 'Pony Diffusion V7',
    parentEcosystemId: ECO.AuraFlow,
    familyId: 9,
    sortOrder: 31,
  },

  // Qwen Family (familyId: 10)
  { id: ECO.Qwen, key: 'Qwen', displayName: 'Qwen', familyId: 10, sortOrder: 90 },
  {
    id: ECO.Qwen2,
    key: 'Qwen2',
    displayName: 'Qwen 2',
    familyId: 10,
    sortOrder: 91,
  },
  {
    id: ECO.Qwen3,
    key: 'Qwen3',
    displayName: 'Qwen 3',
    familyId: 10,
    sortOrder: 92,
  },

  // ZImage Family (familyId: 11)
  {
    id: ECO.ZImageTurbo,
    key: 'ZImageTurbo',
    displayName: 'ZImageTurbo',
    familyId: 11,
    sortOrder: 100,
  },
  {
    id: ECO.ZImageBase,
    key: 'ZImageBase',
    displayName: 'ZImageBase',
    familyId: 11,
    sortOrder: 101,
  },

  // ByteDance Family (familyId: 12)
  {
    id: ECO.Seedream,
    key: 'Seedream',
    displayName: 'Seedream',
    familyId: 12,
    sortOrder: 110,
  },

  // Baidu Family (familyId: 17)
  {
    id: ECO.Ernie,
    key: 'Ernie',
    displayName: 'Ernie',
    familyId: 17,
    sortOrder: 120,
  },

  // Alibaba - Taotian Family (familyId: 18)
  {
    id: ECO.HappyHorse,
    key: 'HappyHorse',
    displayName: 'HappyHorse',
    familyId: 18,
    sortOrder: 130,
  },

  // Krea AI Family (familyId: 20)
  {
    id: ECO.Krea2,
    key: 'Krea2',
    displayName: 'Krea 2',
    familyId: 20,
    sortOrder: 150,
  },

  // Microsoft Family (familyId: 21)
  {
    id: ECO.MAI,
    key: 'MAI',
    displayName: 'MAI',
    familyId: 21,
    sortOrder: 160,
  },
  {
    id: ECO.MageFlow,
    key: 'MageFlow',
    displayName: 'Mage Flow',
    familyId: 21,
    sortOrder: 161,
  },

  // Ideogram Family (familyId: 22)
  {
    id: ECO.Ideogram,
    key: 'Ideogram',
    displayName: 'Ideogram 4.0',
    familyId: 22,
    sortOrder: 170,
  },

  // Boogu Family (familyId: 23)
  {
    id: ECO.Boogu,
    key: 'Boogu',
    displayName: 'Boogu',
    familyId: 23,
    sortOrder: 180,
  },

  // Reve AI Family (familyId: 24)
  {
    id: ECO.Reve,
    key: 'Reve',
    displayName: 'Reve',
    familyId: 24,
    sortOrder: 190,
  },

  // HiDream Family (familyId: 19)
  {
    id: ECO.HiDream,
    key: 'HiDream',
    displayName: 'HiDream',
    familyId: 19,
    sortOrder: 140,
  },
  {
    id: ECO.HiDreamO1,
    key: 'HiDream-O1',
    displayName: 'HiDream-O1',
    familyId: 19,
    sortOrder: 141,
  },

  // Standalone ecosystems (no family)
  { id: ECO.Anima, key: 'Anima', displayName: 'Anima', sortOrder: 199 },
  { id: ECO.AuraFlow, key: 'AuraFlow', displayName: 'AuraFlow', sortOrder: 200 },
  { id: ECO.Chroma, key: 'Chroma', displayName: 'Chroma', sortOrder: 201 },
  {
    id: ECO.CogVideoX,
    key: 'CogVideoX',
    displayName: 'CogVideoX',
    sortOrder: 202,
    // No generation support - training only
  },
  { id: ECO.Kolors, key: 'Kolors', displayName: 'Kolors', sortOrder: 204 },
  {
    id: ECO.LTXV,
    key: 'LTXV',
    displayName: 'LTX Video',
    familyId: 16,
    sortOrder: 205,
  },
  {
    id: ECO.LTXV2,
    key: 'LTXV2',
    displayName: 'LTX Video 2',
    familyId: 16,
    sortOrder: 206,
  },
  {
    id: ECO.LTXV23,
    key: 'LTXV23',
    displayName: 'LTX Video 2.3',
    familyId: 16,
    sortOrder: 207,
  },
  {
    id: ECO.LTXV25,
    key: 'LTXV25',
    displayName: 'LTX Video 2.5',
    familyId: 16,
    sortOrder: 208,
  },
  { id: ECO.Lumina, key: 'Lumina', displayName: 'Lumina', sortOrder: 208 },
  {
    id: ECO.Mochi,
    key: 'Mochi',
    displayName: 'Mochi',
    sortOrder: 209,
  },
  {
    id: ECO.Vidu,
    key: 'Vidu',
    displayName: 'Vidu',
    familyId: 15,
    sortOrder: 210,
    // txt2vid + img2vid (no vid2vid support currently)
  },
  {
    id: ECO.MiniMaxH3,
    key: 'MiniMaxH3',
    displayName: 'Hailuo H3 by MiniMax',
    sortOrder: 211,
    // txt2vid + img2vid (no vid2vid support currently)
  },
  {
    id: ECO.Kling,
    key: 'Kling',
    displayName: 'Kling',
    familyId: 14,
    sortOrder: 212,
    // txt2vid + img2vid (no vid2vid support currently)
  },
  { id: ECO.Grok, key: 'Grok', displayName: 'Grok', familyId: 13, sortOrder: 216 },
  {
    id: ECO.Haiper,
    key: 'Haiper',
    displayName: 'Haiper',
    sortOrder: 213,
    // txt2vid + img2vid
  },
  {
    id: ECO.Lightricks,
    key: 'Lightricks',
    displayName: 'Lightricks',
    familyId: 16,
    sortOrder: 214,
    // txt2vid + img2vid
  },
  {
    id: ECO.Seedance,
    key: 'Seedance',
    displayName: 'Seedance',
    sortOrder: 215,
  },
  { id: ECO.Lens, key: 'Lens', displayName: 'Lens', sortOrder: 207 },
  { id: ECO.ODOR, key: 'ODOR', displayName: 'ODOR', sortOrder: 208 },
  {
    id: ECO.PlaygroundV2,
    key: 'PlaygroundV2',
    displayName: 'Playground v2',
    sortOrder: 209,
  },
  {
    id: ECO.Upscaler,
    key: 'Upscaler',
    displayName: 'Upscaler',
    sortOrder: 998,
  },
  {
    id: ECO.Other,
    key: 'Other',
    displayName: 'Other',
    sortOrder: 999,
  },

  // Audio ecosystems
  {
    id: ECO.AceAudio,
    key: 'Ace',
    displayName: 'ACE Audio',
    sortOrder: 300,
  },

  // 3D Model ecosystems
  {
    id: ECO.PolyGen,
    key: 'PolyGen',
    displayName: 'PolyGen (Meshy)',
    sortOrder: 301,
  },
  {
    id: ECO.Tripo,
    key: 'Tripo',
    displayName: 'Tripo',
    sortOrder: 302,
  },
  {
    id: ECO.Hunyuan3D,
    key: 'Hunyuan3D',
    displayName: 'Hunyuan3D',
    sortOrder: 303,
  },
];

export const ecosystemById = new Map(ecosystems.map((e) => [e.id, e]));
export const ecosystemByKey = new Map(ecosystems.map((e) => [e.key, e]));

/**
 * Ecosystems whose generation output is a 3D model (`polyGen` step). These
 * share the `model3d` output type, are resource-less, and are enumerated
 * explicitly because the Prisma `MediaType` enum has no `model3d` variant
 * (their `BaseModelRecord`s register as `type: 'image'`). Single source of
 * truth for the 3D ecosystem picker filter in `getEcosystemDisplayItems`.
 */
export const MODEL3D_ECOSYSTEM_IDS = new Set<number>([ECO.PolyGen, ECO.Tripo, ECO.Hunyuan3D]);

/** Ecosystem keys for the 3D-model ecosystems (`model3d` output). */
export const MODEL3D_ECOSYSTEM_KEYS = new Set<string>(
  [...MODEL3D_ECOSYSTEM_IDS].map((id) => ecosystemById.get(id)?.key).filter((k): k is string => !!k)
);

/**
 * Ecosystem keys whose generation routes to Civitai-hosted GPUs/workers rather
 * than an external provider. Single source of truth for the self-hosted
 * generation toggle. Derived from the orchestrator ecosystem handlers
 * (`src/server/services/orchestrator/ecosystems/`) — grouped by the
 * `@civitai/client` input type each ecosystem produces:
 *
 *  - TextToImageInput    → SD1/2/XL, Pony, Illustrious, NoobAI, Flux1, FluxKrea,
 *                          Chroma, HiDream, PonyV7
 *  - ComfyImageGenInput  → Anima, Ernie, Lens, HiDream-O1
 *  - SdCppImageGenInput  → ZImageTurbo, ZImageBase, Qwen
 *  - Flux2KleinImageGen  → Flux2Klein_9B(_base), Flux2Klein_4B(_base)
 *  - ComfyLtx*VideoGen   → LTXV2, LTXV23, LTXV25
 *  - AceStepAudioInput   → Ace
 *
 * NOTE: lookalikes that are EXTERNAL and must NOT be listed — `Flux2` (≠ Klein),
 * `Qwen2` (≠ Qwen, FAL), `Qwen3` (≠ Qwen, Alibaba DashScope), and all `Wan*`
 * (currently FAL). Keep this in sync when an
 * ecosystem's routing changes.
 */
export const SELF_HOSTED_ECOSYSTEM_KEYS = [
  // TextToImageInput
  'SD1',
  'SD2',
  'SDXL',
  'Pony',
  'Illustrious',
  'NoobAI',
  'Flux1',
  'FluxKrea',
  'Chroma',
  'HiDream',
  'PonyV7',
  // ComfyImageGenInput
  'Anima',
  'Ernie',
  'Lens',
  'HiDream-O1',
  // SdCppImageGenInput
  'ZImageTurbo',
  'ZImageBase',
  'Qwen',
  // Flux2KleinImageGenInput
  'Flux2Klein_9B',
  'Flux2Klein_9B_base',
  'Flux2Klein_4B',
  'Flux2Klein_4B_base',
  // ComfyLtx2VideoGenInput / ComfyLtx23VideoGenInput / ComfyLtx25VideoGenInput
  'LTXV2',
  'LTXV23',
  'LTXV25',
  // AceStepAudioInput
  'Ace',
  // Hunyuan3dComfyPolyGenInput (3D; Meshy/Tripo are FAL and stay external)
  'Hunyuan3D',
] as const;

const selfHostedEcosystemKeySet = new Set<string>(SELF_HOSTED_ECOSYSTEM_KEYS);

// Stamp the flag onto the records so it travels with the ecosystem data.
for (const e of ecosystems) {
  if (selfHostedEcosystemKeySet.has(e.key)) e.selfHosted = true;
}

export const isSelfHostedEcosystem = (key: string | null | undefined): boolean =>
  !!key && selfHostedEcosystemKeySet.has(key);

// =============================================================================
// Ecosystem Support
// =============================================================================

const fullAddonTypes = [
  ModelType.Checkpoint,
  ModelType.LORA,
  ModelType.DoRA,
  ModelType.LoCon,
  ModelType.VAE,
  ModelType.TextualInversion,
];

const checkpointAndLora = [ModelType.Checkpoint, ModelType.LORA];
const checkpointOnly = [ModelType.Checkpoint];
const loraOnly = [ModelType.LORA];
/** Addon types that cross between SDXL parent ↔ child ecosystems */
const sdxlCrossAddonTypes = [
  ModelType.TextualInversion,
  ModelType.LORA,
  ModelType.DoRA,
  ModelType.LoCon,
  ModelType.VAE,
];
/** Addon types that cross between SDXL sibling ecosystems (no VAE) */
const sdxlSiblingAddonTypes = [
  ModelType.TextualInversion,
  ModelType.LORA,
  ModelType.DoRA,
  ModelType.LoCon,
];

export const ecosystemSupport: EcosystemSupport[] = [
  // SD1 - full addon support
  { ecosystemId: ECO.SD1, supportType: 'generation', modelTypes: fullAddonTypes },
  { ecosystemId: ECO.SD1, supportType: 'training', modelTypes: loraOnly },

  // SDXL - full addon support (Pony, Illustrious, NoobAI inherit this)
  { ecosystemId: ECO.SDXL, supportType: 'generation', modelTypes: fullAddonTypes },
  { ecosystemId: ECO.SDXL, supportType: 'training', modelTypes: loraOnly },
  { ecosystemId: ECO.SDXL, supportType: 'auction', modelTypes: checkpointAndLora },

  // Flux1 - checkpoint and LORA only
  { ecosystemId: ECO.Flux1, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.Flux1, supportType: 'training', modelTypes: loraOnly },

  // FluxKrea - checkpoint and LORA
  { ecosystemId: ECO.FluxKrea, supportType: 'generation', modelTypes: checkpointAndLora },

  // Flux1Kontext - checkpoint only (no LORA)
  { ecosystemId: ECO.Flux1Kontext, supportType: 'generation', modelTypes: checkpointOnly },

  // Flux2 - checkpoint and LORA
  { ecosystemId: ECO.Flux2, supportType: 'generation', modelTypes: checkpointAndLora },

  // Flux2 Klein variants - checkpoint and LORA
  { ecosystemId: ECO.Flux2Klein_9B, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.Flux2Klein_9B_base, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.Flux2Klein_4B, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.Flux2Klein_4B_base, supportType: 'generation', modelTypes: checkpointAndLora },

  // Chroma - full addon support
  { ecosystemId: ECO.Chroma, supportType: 'generation', modelTypes: fullAddonTypes },
  { ecosystemId: ECO.Chroma, supportType: 'training', modelTypes: loraOnly },

  // Qwen - checkpoint and LORA
  { ecosystemId: ECO.Qwen, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.Qwen, supportType: 'training', modelTypes: loraOnly },

  // Qwen 2 - checkpoint only
  { ecosystemId: ECO.Qwen2, supportType: 'generation', modelTypes: [ModelType.Checkpoint] },

  // Qwen 3 - checkpoint only
  { ecosystemId: ECO.Qwen3, supportType: 'generation', modelTypes: [ModelType.Checkpoint] },

  // HyV1 (Hunyuan Video) - LORA only
  { ecosystemId: ECO.HyV1, supportType: 'generation', modelTypes: loraOnly },

  // WanVideo ecosystems
  // { ecosystemId: ECO.WanVideo, supportType: 'generation', modelTypes: loraOnly }, // This shouldn't ever apply
  { ecosystemId: ECO.WanVideo14B_T2V, supportType: 'generation', modelTypes: checkpointAndLora },
  {
    ecosystemId: ECO.WanVideo14B_I2V_480p,
    supportType: 'generation',
    modelTypes: checkpointAndLora,
  },
  {
    ecosystemId: ECO.WanVideo14B_I2V_720p,
    supportType: 'generation',
    modelTypes: checkpointAndLora,
  },
  { ecosystemId: ECO.WanVideo22_TI2V_5B, supportType: 'generation', modelTypes: checkpointAndLora },
  {
    ecosystemId: ECO.WanVideo22_I2V_A14B,
    supportType: 'generation',
    modelTypes: checkpointAndLora,
  },
  {
    ecosystemId: ECO.WanVideo22_T2V_A14B,
    supportType: 'generation',
    modelTypes: checkpointAndLora,
  },
  { ecosystemId: ECO.WanVideo25_T2V, supportType: 'generation', modelTypes: checkpointOnly },
  { ecosystemId: ECO.WanVideo25_I2V, supportType: 'generation', modelTypes: checkpointOnly },
  { ecosystemId: ECO.WanImage27, supportType: 'generation', modelTypes: checkpointOnly },
  { ecosystemId: ECO.WanVideo27, supportType: 'generation', modelTypes: checkpointOnly },
  { ecosystemId: ECO.WanVideo30, supportType: 'generation', modelTypes: checkpointOnly },

  // HiDream - checkpoint and LORA
  { ecosystemId: ECO.HiDream, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.HiDream, supportType: 'training', modelTypes: loraOnly },

  // HiDream-O1 - checkpoint and LORA
  { ecosystemId: ECO.HiDreamO1, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.HiDreamO1, supportType: 'training', modelTypes: loraOnly },

  // NanoBanana - checkpoint only
  { ecosystemId: ECO.NanoBanana, supportType: 'generation', modelTypes: checkpointOnly },

  // OpenAI - checkpoint only
  { ecosystemId: ECO.OpenAI, supportType: 'generation', modelTypes: checkpointOnly },

  // Imagen4 - checkpoint only
  { ecosystemId: ECO.Imagen4, supportType: 'generation', modelTypes: checkpointOnly },

  // Veo3 - checkpoint only
  { ecosystemId: ECO.Veo3, supportType: 'generation', modelTypes: checkpointOnly },

  // Grok - checkpoint only
  { ecosystemId: ECO.Grok, supportType: 'generation', modelTypes: checkpointOnly },

  // Seedream - checkpoint only
  { ecosystemId: ECO.Seedream, supportType: 'generation', modelTypes: checkpointOnly },

  // Ernie - checkpoint and LORA
  { ecosystemId: ECO.Ernie, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.Ernie, supportType: 'training', modelTypes: loraOnly },

  // Krea 2 - checkpoint locked, but base/turbo comfy variants support LoRA (medium/large FAL tiers do not); LoRA training via AI-Toolkit
  {
    ecosystemId: ECO.Krea2,
    supportType: 'generation',
    modelTypes: [ModelType.Checkpoint, ModelType.LORA, ModelType.VAE],
  },
  { ecosystemId: ECO.Krea2, supportType: 'training', modelTypes: loraOnly },

  // MAI - checkpoint only (Microsoft MAI-Image-2.5, locked, no LoRA support)
  { ecosystemId: ECO.MAI, supportType: 'generation', modelTypes: checkpointOnly },

  // Reve - checkpoint only (Reve 2.1, locked, FAL engine, no LoRA support)
  { ecosystemId: ECO.Reve, supportType: 'generation', modelTypes: checkpointOnly },

  // MageFlow - checkpoint only (Microsoft Mage Flow, six official builds, no community LoRAs yet)
  { ecosystemId: ECO.MageFlow, supportType: 'generation', modelTypes: checkpointOnly },

  // Lens - checkpoint and LORA (Civitai-internal, normal + turbo variants)
  { ecosystemId: ECO.Lens, supportType: 'generation', modelTypes: checkpointAndLora },

  // Sora2 - checkpoint only
  { ecosystemId: ECO.Sora2, supportType: 'generation', modelTypes: checkpointOnly },

  // Mochi - checkpoint only
  // { ecosystemId: ECO.Mochi, supportType: 'generation', modelTypes: checkpointOnly },

  // Vidu - checkpoint only
  { ecosystemId: ECO.Vidu, supportType: 'generation', modelTypes: checkpointOnly },

  // MiniMax H3 (Hailuo) - LoRAs apply to the comfy variant only (the hosted API
  // build takes no resources); LORA training is AI Toolkit only
  { ecosystemId: ECO.MiniMaxH3, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.MiniMaxH3, supportType: 'training', modelTypes: loraOnly },

  // Kling - checkpoint only
  { ecosystemId: ECO.Kling, supportType: 'generation', modelTypes: checkpointOnly },

  // Seedance - checkpoint only
  { ecosystemId: ECO.Seedance, supportType: 'generation', modelTypes: checkpointOnly },

  // HappyHorse - checkpoint only
  { ecosystemId: ECO.HappyHorse, supportType: 'generation', modelTypes: checkpointOnly },

  // Flux 3 Video - checkpoint only (BFL via FAL, closed weights)
  { ecosystemId: ECO.Flux3Video, supportType: 'generation', modelTypes: checkpointOnly },

  // Anima - checkpoint, LORA, LoCon, DoRA and VAE generation, LORA training
  {
    ecosystemId: ECO.Anima,
    supportType: 'generation',
    modelTypes: [
      ModelType.Checkpoint,
      ModelType.LORA,
      ModelType.LoCon,
      ModelType.DoRA,
      ModelType.VAE,
    ],
  },
  { ecosystemId: ECO.Anima, supportType: 'training', modelTypes: loraOnly },

  // Boogu - LORA training (AI Toolkit only)
  { ecosystemId: ECO.Boogu, supportType: 'training', modelTypes: loraOnly },

  // Mage-Flow - LORA training (AI Toolkit only)
  { ecosystemId: ECO.MageFlow, supportType: 'training', modelTypes: loraOnly },

  // Ideogram 4 - LORA training (AI Toolkit only)
  { ecosystemId: ECO.Ideogram, supportType: 'training', modelTypes: loraOnly },

  // PonyV7 - checkpoint and LORA (based on AuraFlow)
  { ecosystemId: ECO.PonyV7, supportType: 'generation', modelTypes: checkpointAndLora },

  // ZImageTurbo - checkpoint and LORA
  { ecosystemId: ECO.ZImageTurbo, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.ZImageTurbo, supportType: 'training', modelTypes: loraOnly },
  { ecosystemId: ECO.ZImageTurbo, supportType: 'auction', modelTypes: checkpointAndLora },

  // ZImageBase - checkpoint and LORA
  { ecosystemId: ECO.ZImageBase, supportType: 'generation', modelTypes: checkpointAndLora },
  { ecosystemId: ECO.ZImageBase, supportType: 'training', modelTypes: loraOnly },
  { ecosystemId: ECO.ZImageBase, supportType: 'auction', modelTypes: checkpointAndLora },

  // Boogu - checkpoint and LORA (training upcoming per orchestrator)
  { ecosystemId: ECO.Boogu, supportType: 'generation', modelTypes: checkpointAndLora },

  // LTXV - checkpoint only (parent ecosystem)
  { ecosystemId: ECO.LTXV, supportType: 'generation', modelTypes: checkpointOnly },

  // LTXV2 - checkpoint and LORA
  { ecosystemId: ECO.LTXV2, supportType: 'generation', modelTypes: checkpointAndLora },

  // LTXV2.3 - checkpoint and LORA
  { ecosystemId: ECO.LTXV23, supportType: 'generation', modelTypes: checkpointAndLora },

  // LTXV2.5 - checkpoint and LORA
  { ecosystemId: ECO.LTXV25, supportType: 'generation', modelTypes: checkpointAndLora },

  // AceAudio - checkpoint only (audio generation)
  { ecosystemId: ECO.AceAudio, supportType: 'generation', modelTypes: checkpointOnly },

  // PolyGen - remote 3D generator (Meshy via Fal). No Civitai checkpoint/LoRA;
  // entry exists so the unified generator picker can route 3D-Models workflows
  // and the dev-time `getEcosystemSupport` audit in workflows.ts stays clean.
  { ecosystemId: ECO.PolyGen, supportType: 'generation', modelTypes: [] },

  // Tripo / Hunyuan3D - remote 3D generators (Fal / Comfy). Like PolyGen, no
  // Civitai checkpoint/LoRA; entries exist so the unified generator picker can
  // route the img2model3d workflow to these ecosystems.
  { ecosystemId: ECO.Tripo, supportType: 'generation', modelTypes: [] },
  { ecosystemId: ECO.Hunyuan3D, supportType: 'generation', modelTypes: [] },

  // Upscaler - upscaler models only
  { ecosystemId: ECO.Upscaler, supportType: 'generation', modelTypes: [ModelType.Upscaler] },
];

// =============================================================================
// Ecosystem Settings
// =============================================================================

export const ecosystemSettings: EcosystemSettings[] = [
  {
    ecosystemId: ECO.SD1,
    defaults: {
      model: { id: 128713 },
    },
  },
  {
    ecosystemId: ECO.SDXL,
    defaults: {
      model: { id: 128078 },
    },
  },
  {
    ecosystemId: ECO.Pony,
    defaults: {
      model: { id: 290640 },
    },
  },
  {
    ecosystemId: ECO.Illustrious,
    defaults: {
      model: { id: 889818 },
    },
  },
  {
    ecosystemId: ECO.NoobAI,
    defaults: {
      model: { id: 1190596 },
    },
  },
  {
    ecosystemId: ECO.Flux1,
    defaults: {
      model: { id: 691639 },
    },
  },
  {
    ecosystemId: ECO.FluxKrea,
    defaults: {
      model: { id: 2068000 },
    },
  },
  {
    ecosystemId: ECO.Flux1Kontext,
    defaults: {
      model: { id: 1892509 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Flux2,
    defaults: {
      model: { id: 2439067 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Flux2Klein_9B,
    defaults: {
      model: { id: 2612554 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Flux2Klein_9B_base,
    defaults: {
      model: { id: 2612548 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Flux2Klein_4B,
    defaults: {
      model: { id: 2612557 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Flux2Klein_4B_base,
    defaults: {
      model: { id: 2612552 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Anima,
    defaults: {
      model: { id: 2945208 },
    },
  },
  {
    ecosystemId: ECO.Krea2,
    defaults: {
      model: { id: 3072329 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.MAI,
    defaults: {
      model: { id: 3002140 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Chroma,
    defaults: {
      model: { id: 2164239 },
    },
  },
  {
    ecosystemId: ECO.HiDream,
    defaults: {
      model: { id: 1771369 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.HiDreamO1,
    defaults: {
      model: { id: 2939964 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Qwen,
    defaults: {
      model: { id: 2552908 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Qwen2,
    defaults: {
      model: { id: 2744101 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Qwen3,
    defaults: {
      model: { id: 3207633 },
      modelLocked: true,
      engine: 'qwen',
    },
  },
  {
    ecosystemId: ECO.HyV1,
    defaults: {
      model: { id: 1314512 },
      modelLocked: true,
      engine: 'hunyuan',
    },
  },
  {
    ecosystemId: ECO.WanVideo,
    defaults: {
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo1_3B_T2V,
    defaults: {
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo14B_T2V,
    defaults: {
      model: { id: 1707796 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo14B_I2V_480p,
    defaults: {
      model: { id: 1501125 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo14B_I2V_720p,
    defaults: {
      model: { id: 1501344 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo22_TI2V_5B,
    defaults: {
      model: { id: 2114110 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo22_I2V_A14B,
    defaults: {
      model: { id: 2114157 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo22_T2V_A14B,
    defaults: {
      model: { id: 2114154 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo25_T2V,
    defaults: {
      model: { id: 2254989 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo25_I2V,
    defaults: {
      model: { id: 2254963 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanImage27,
    defaults: {
      model: { id: 2828170 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo27,
    defaults: {
      model: { id: 2828005 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.WanVideo30,
    defaults: {
      model: { id: 3267095 },
      modelLocked: true,
      engine: 'wan',
    },
  },
  {
    ecosystemId: ECO.Mochi,
    defaults: {
      model: { id: 1034189 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.LTXV,
    defaults: {
      model: { id: 1499827 },
      modelLocked: true,
      engine: 'lightricks',
    },
  },
  {
    ecosystemId: ECO.LTXV2,
    defaults: {
      model: { id: 2578325 },
      modelLocked: true,
      engine: 'ltx2',
    },
  },
  {
    ecosystemId: ECO.LTXV23,
    defaults: {
      model: { id: 2749908 },
      modelLocked: true,
      engine: 'ltx2',
    },
  },
  {
    ecosystemId: ECO.LTXV25,
    defaults: {
      model: { id: 3220143 },
      modelLocked: true,
      engine: 'ltx2',
    },
  },
  {
    ecosystemId: ECO.Veo3,
    defaults: {
      model: { id: 2827945 },
      modelLocked: true,
      engine: 'veo3',
    },
  },
  {
    ecosystemId: ECO.NanoBanana,
    defaults: {
      model: { id: 2154472 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.OpenAI,
    defaults: {
      model: { id: 1733399 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Imagen4,
    defaults: {
      model: { id: 1889632 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Seedream,
    defaults: {
      model: { id: 3110984 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.ZImageTurbo,
    defaults: {
      model: { id: 2442439 },
    },
  },
  {
    ecosystemId: ECO.ZImageBase,
    defaults: {
      model: { id: 2635223 },
    },
  },
  {
    ecosystemId: ECO.Boogu,
    defaults: {
      model: { id: 3049541 },
    },
  },
  {
    ecosystemId: ECO.PonyV7,
    defaults: {
      model: { id: 2152373 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Sora2,
    defaults: {
      model: { id: 2320065 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Vidu,
    defaults: {
      model: { id: 2623839 },
      modelLocked: true,
      engine: 'vidu',
    },
  },
  {
    ecosystemId: ECO.MiniMaxH3,
    defaults: {
      // The comfy variant, not the hosted API one: it is the version the model
      // page publishes, and the only one that takes LoRAs.
      model: { id: 3216500 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Kling,
    defaults: {
      model: { id: 2698632 },
      modelLocked: true,
      engine: 'kling',
    },
  },
  {
    ecosystemId: ECO.Seedance,
    defaults: {
      model: { id: 2864671 },
      modelLocked: true,
      engine: 'seedance',
    },
  },
  {
    ecosystemId: ECO.Flux3Video,
    defaults: {
      model: { id: 3204701 },
      modelLocked: true,
      engine: 'flux',
    },
  },
  {
    ecosystemId: ECO.Grok,
    defaults: {
      model: { id: 2738377 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Ernie,
    defaults: {
      model: { id: 2863858 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.HappyHorse,
    defaults: {
      // Default to the newest version (v1.1 = 3063263); v1.0 (2902378) remains
      // selectable via the version picker in happy-horse-graph.ts.
      model: { id: 3063263 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Other,
    defaults: {
      model: { id: 164821 },
    },
  },
  {
    ecosystemId: ECO.AceAudio,
    defaults: {
      model: { id: 2864949 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Lens,
    defaults: {
      model: { id: 2982236 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.Reve,
    defaults: {
      model: { id: 3133202 },
      modelLocked: true,
    },
  },
  {
    ecosystemId: ECO.MageFlow,
    defaults: {
      model: { id: 3172038 },
      modelLocked: true,
    },
  },
];

// =============================================================================
// Cross-Ecosystem Rules
// =============================================================================

export const crossEcosystemRules: CrossEcosystemRule[] = [
  // ==========================================================================
  // SD1 → SDXL Family
  // ==========================================================================
  // SD1 TextualInversion works in SDXL family (and its children: Pony, Illustrious, NoobAI)
  {
    sourceEcosystemId: ECO.SD1,
    targetEcosystemId: ECO.SDXL,
    supportType: 'generation',
    modelTypes: [ModelType.TextualInversion],
    support: 'partial',
  },
  // SD1 TextualInversion also works directly in Pony
  {
    sourceEcosystemId: ECO.SD1,
    targetEcosystemId: ECO.Pony,
    supportType: 'generation',
    modelTypes: [ModelType.TextualInversion],
    support: 'partial',
  },
  // SD1 TextualInversion also works directly in Illustrious
  {
    sourceEcosystemId: ECO.SD1,
    targetEcosystemId: ECO.Illustrious,
    supportType: 'generation',
    modelTypes: [ModelType.TextualInversion],
    support: 'partial',
  },
  // SD1 TextualInversion also works directly in NoobAI
  {
    sourceEcosystemId: ECO.SD1,
    targetEcosystemId: ECO.NoobAI,
    supportType: 'generation',
    modelTypes: [ModelType.TextualInversion],
    support: 'partial',
  },

  // ==========================================================================
  // SDXL ↔ Pony/Illustrious/NoobAI (parent ↔ child)
  // ==========================================================================
  // SDXL addons work in Pony/Illustrious/NoobAI and vice versa
  {
    sourceEcosystemId: ECO.SDXL,
    targetEcosystemId: ECO.Pony,
    supportType: 'generation',
    modelTypes: sdxlCrossAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.Pony,
    targetEcosystemId: ECO.SDXL,
    supportType: 'generation',
    modelTypes: sdxlCrossAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.SDXL,
    targetEcosystemId: ECO.Illustrious,
    supportType: 'generation',
    modelTypes: sdxlCrossAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.Illustrious,
    targetEcosystemId: ECO.SDXL,
    supportType: 'generation',
    modelTypes: sdxlCrossAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.SDXL,
    targetEcosystemId: ECO.NoobAI,
    supportType: 'generation',
    modelTypes: sdxlCrossAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.NoobAI,
    targetEcosystemId: ECO.SDXL,
    supportType: 'generation',
    modelTypes: sdxlCrossAddonTypes,
    support: 'partial',
  },

  // ==========================================================================
  // Pony ↔ Illustrious ↔ NoobAI (siblings)
  // ==========================================================================
  {
    sourceEcosystemId: ECO.Pony,
    targetEcosystemId: ECO.Illustrious,
    supportType: 'generation',
    modelTypes: sdxlSiblingAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.Illustrious,
    targetEcosystemId: ECO.Pony,
    supportType: 'generation',
    modelTypes: sdxlSiblingAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.Pony,
    targetEcosystemId: ECO.NoobAI,
    supportType: 'generation',
    modelTypes: sdxlSiblingAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.NoobAI,
    targetEcosystemId: ECO.Pony,
    supportType: 'generation',
    modelTypes: sdxlSiblingAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.Illustrious,
    targetEcosystemId: ECO.NoobAI,
    supportType: 'generation',
    modelTypes: sdxlSiblingAddonTypes,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.NoobAI,
    targetEcosystemId: ECO.Illustrious,
    supportType: 'generation',
    modelTypes: sdxlSiblingAddonTypes,
    support: 'partial',
  },

  // ==========================================================================
  // Flux Family Cross-Support
  // ==========================================================================
  // Flux1 Checkpoint+LORA works partially in FluxKrea
  {
    sourceEcosystemId: ECO.Flux1,
    targetEcosystemId: ECO.FluxKrea,
    supportType: 'generation',
    modelTypes: checkpointAndLora,
    support: 'partial',
  },
  // FluxKrea Checkpoint+LORA works partially in Flux1
  {
    sourceEcosystemId: ECO.FluxKrea,
    targetEcosystemId: ECO.Flux1,
    supportType: 'generation',
    modelTypes: checkpointAndLora,
    support: 'partial',
  },

  // ==========================================================================
  // WanVideo 14B ↔ WanVideo 2.2 Cross-Support
  // ==========================================================================
  // WanVideo 2.2 LORA works partially in WanVideo 14B T2V
  {
    sourceEcosystemId: ECO.WanVideo22_T2V_A14B,
    targetEcosystemId: ECO.WanVideo14B_T2V,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo22_I2V_A14B,
    targetEcosystemId: ECO.WanVideo14B_T2V,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo22_TI2V_5B,
    targetEcosystemId: ECO.WanVideo14B_T2V,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // WanVideo 2.2 LORA works partially in WanVideo 14B I2V 480p
  {
    sourceEcosystemId: ECO.WanVideo22_T2V_A14B,
    targetEcosystemId: ECO.WanVideo14B_I2V_480p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo22_I2V_A14B,
    targetEcosystemId: ECO.WanVideo14B_I2V_480p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo22_TI2V_5B,
    targetEcosystemId: ECO.WanVideo14B_I2V_480p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // WanVideo 2.2 LORA works partially in WanVideo 14B I2V 720p
  {
    sourceEcosystemId: ECO.WanVideo22_T2V_A14B,
    targetEcosystemId: ECO.WanVideo14B_I2V_720p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo22_I2V_A14B,
    targetEcosystemId: ECO.WanVideo14B_I2V_720p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo22_TI2V_5B,
    targetEcosystemId: ECO.WanVideo14B_I2V_720p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // WanVideo 14B LORA works partially in WanVideo 2.2 T2V
  {
    sourceEcosystemId: ECO.WanVideo14B_T2V,
    targetEcosystemId: ECO.WanVideo22_T2V_A14B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_480p,
    targetEcosystemId: ECO.WanVideo22_T2V_A14B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_720p,
    targetEcosystemId: ECO.WanVideo22_T2V_A14B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // WanVideo 14B LORA works partially in WanVideo 2.2 I2V
  {
    sourceEcosystemId: ECO.WanVideo14B_T2V,
    targetEcosystemId: ECO.WanVideo22_I2V_A14B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_480p,
    targetEcosystemId: ECO.WanVideo22_I2V_A14B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_720p,
    targetEcosystemId: ECO.WanVideo22_I2V_A14B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // WanVideo 14B LORA works partially in WanVideo 2.2 TI2V
  {
    sourceEcosystemId: ECO.WanVideo14B_T2V,
    targetEcosystemId: ECO.WanVideo22_TI2V_5B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_480p,
    targetEcosystemId: ECO.WanVideo22_TI2V_5B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_720p,
    targetEcosystemId: ECO.WanVideo22_TI2V_5B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // ==========================================================================
  // WanVideo 14B I2V 480p ↔ 720p Cross-Support
  // ==========================================================================
  // These two I2V models share LORA compatibility
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_480p,
    targetEcosystemId: ECO.WanVideo14B_I2V_720p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  {
    sourceEcosystemId: ECO.WanVideo14B_I2V_720p,
    targetEcosystemId: ECO.WanVideo14B_I2V_480p,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // ==========================================================================
  // ZImage Family Cross-Support
  // ==========================================================================
  // ZImageBase LORA works partially in ZImageTurbo
  {
    sourceEcosystemId: ECO.ZImageBase,
    targetEcosystemId: ECO.ZImageTurbo,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // ==========================================================================
  // Flux2 Klein Cross-Support
  // ==========================================================================
  // Flux2 Klein 4B has partial support for Flux2 Klein 4B-base
  {
    sourceEcosystemId: ECO.Flux2Klein_4B_base,
    targetEcosystemId: ECO.Flux2Klein_4B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
  // Flux2 Klein 9B has partial support for Flux2 Klein 9B-base
  {
    sourceEcosystemId: ECO.Flux2Klein_9B_base,
    targetEcosystemId: ECO.Flux2Klein_9B,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // ==========================================================================
  // AuraFlow ↔ PonyV7 (parent ↔ child)
  // ==========================================================================
  // AuraFlow LoRA works partially in PonyV7
  {
    sourceEcosystemId: ECO.AuraFlow,
    targetEcosystemId: ECO.PonyV7,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },

  // ==========================================================================
  // LTXV 2.3 → LTXV 2.5
  // ==========================================================================
  // LTXV 2.3 LoRA works partially in LTXV 2.5 — surfaced in the resource picker
  // only with Advanced mode on, and flagged as partially compatible.
  {
    sourceEcosystemId: ECO.LTXV23,
    targetEcosystemId: ECO.LTXV25,
    supportType: 'generation',
    modelTypes: loraOnly,
    support: 'partial',
  },
];

// =============================================================================
// Support Overrides
// =============================================================================

// Base model ID constants
export const BM = {
  AuraFlow: 1,
  Chroma: 2,
  CogVideoX: 3,
  Flux1S: 4,
  Flux1D: 5,
  Flux1Krea: 6,
  Flux1Kontext: 7,
  Flux2D: 8,
  Flux2Klein_9B: 73,
  Flux2Klein_9B_base: 74,
  Flux2Klein_4B: 75,
  Flux2Klein_4B_base: 76,
  HiDream: 9,
  HiDreamO1: 87,
  Hunyuan1: 10,
  HunyuanVideo: 11,
  Illustrious: 12,
  Imagen4: 13,
  Kolors: 14,
  LTXV: 15,
  LTXV2: 72,
  LTXV23: 80,
  LTXV25: 100,
  Lumina: 16,
  Mochi: 17,
  NanoBanana: 18,
  NoobAI: 19,
  ODOR: 20,
  OpenAI: 21,
  Other: 22,
  PixArtA: 23,
  PixArtE: 24,
  PlaygroundV2: 25,
  Pony: 26,
  PonyV7: 27,
  Qwen: 28,
  StableCascade: 29,
  SD14: 30,
  SD15: 31,
  SD15LCM: 32,
  SD15Hyper: 33,
  SD20: 34,
  SD20_768: 35,
  SD21: 36,
  SD21_768: 37,
  SD21Unclip: 38,
  SD3: 39,
  SD35: 40,
  SD35Large: 41,
  SD35LargeTurbo: 42,
  SD35Medium: 43,
  SDXL09: 44,
  SDXL10: 45,
  SDXL10LCM: 46,
  SDXLLightning: 47,
  SDXLHyper: 48,
  SDXLTurbo: 49,
  SDXLDistilled: 50,
  Seedream: 51,
  SVD: 52,
  SVDXT: 53,
  Sora2: 54,
  Veo3: 55,
  WanVideo: 56,
  WanVideo13BT2V: 57,
  WanVideo14BT2V: 58,
  WanVideo14BI2V480p: 59,
  WanVideo14BI2V720p: 60,
  WanVideo22TI2V5B: 61,
  WanVideo22I2VA14B: 62,
  WanVideo22T2VA14B: 63,
  WanVideo25T2V: 64,
  WanVideo25I2V: 65,
  ZImageTurbo: 66,
  ZImageBase: 71,
  Vidu: 67,
  MiniMaxH3: 68,
  Kling: 69,
  Seedance: 70,
  Anima: 77,
  Grok: 78,
  Qwen2: 79,
  Qwen3: 99,
  WanImage27: 86,
  WanVideo27: 81,
  WanVideo30: 101,
  Upscaler: 82,
  Ernie: 83,
  AceAudio: 84,
  HappyHorse: 85,
  Lens: 88,
  Krea2: 89,
  MAI: 90,
  Ideogram: 91,
  Boogu: 93,
  // PolyGen was originally 90 on the hackaton branch; bumped to 91 to dodge
  // MAI (main-line), then bumped again to 92 on the main merge to dodge
  // Ideogram (also main-line).
  PolyGen: 92,
  Tripo: 94,
  Hunyuan3D: 95,
  Reve: 96,
  MageFlow: 97,
  Flux3Video: 98,
} as const;

// Guard against duplicate ids — `baseModelById` is keyed by id, so collisions
// silently overwrite earlier entries and cause cross-ecosystem mis-routing.
(() => {
  const seen = new Map<number, string>();
  for (const [k, v] of Object.entries(BM)) {
    const prior = seen.get(v);
    if (prior !== undefined) throw new Error(`Duplicate BM id ${v}: ${prior} and ${k}`);
    seen.set(v, k);
  }
})();

export const supportOverrides: SupportOverride[] = [
  // NOTE: Models with `disabled: true` on BaseModelRecord don't need entries here.
  // The disabled flag provides root-level disable for all support types.
  // Disabled models: SD3, SD35, SD35Large, SD35LargeTurbo, SD35Medium, SDXLTurbo, SVD, SVDXT
  // Group-level overrides (for when only specific support types should be disabled)
  // SD3 group - no training (redundant since all SD3 models are disabled, but kept for documentation)
  // { ecosystemId: ECO.SD3, supportType: 'training', enabled: false },
  // { ecosystemId: ECO.SD35M, supportType: 'training', enabled: false },
];

// =============================================================================
// Licenses (unchanged from v1)
// =============================================================================

export const licenses: LicenseRecord[] = [
  {
    id: 1,
    name: 'CreativeML Open RAIL-M',
    url: 'https://huggingface.co/spaces/CompVis/stable-diffusion-license',
  },
  {
    id: 2,
    name: 'SDXL 0.9 research license',
    url: 'https://github.com/Stability-AI/generative-models/blob/main/model_licenses/LICENSE-SDXL0.9',
  },
  {
    id: 3,
    name: 'CreativeML Open RAIL++-M',
    url: 'https://github.com/Stability-AI/generative-models/blob/main/model_licenses/LICENSE-SDXL1.0',
  },
  {
    id: 4,
    name: 'Stability AI Non-Commercial Research Community License',
    url: 'https://huggingface.co/stabilityai/sdxl-turbo/blob/main/LICENSE.md',
    notice:
      'This Stability AI Model is licensed under the Stability AI Non-Commercial Research Community License, Copyright (c) Stability AI Ltd. All Rights Reserved.',
    poweredBy: 'Powered by Stability AI',
    disableMature: true,
  },
  {
    id: 5,
    name: 'Stable Video Diffusion Community License',
    url: 'https://huggingface.co/stabilityai/stable-video-diffusion-img2vid-xt/blob/main/LICENSE.md',
    notice:
      'Stable Video Diffusion is licensed under the Stable Video Diffusion Community License, Copyright (c) Stability AI Ltd. All Rights Reserved.',
    poweredBy: 'Powered by Stability AI',
    disableMature: true,
  },
  {
    id: 6,
    name: 'Playground v2 Community License',
    url: 'https://huggingface.co/playgroundai/playground-v2-1024px-aesthetic/blob/main/LICENSE.md',
  },
  {
    id: 7,
    name: 'AGPL-3.0',
    url: 'https://github.com/PixArt-alpha/PixArt-alpha/blob/master/LICENSE',
  },
  {
    id: 8,
    name: 'SAI NC RC',
    url: 'https://huggingface.co/stabilityai/stable-cascade/blob/main/LICENSE',
    notice:
      'Stable Cascade is licensed under the Stable Cascade Non-Commercial Community License, Copyright (c) Stability AI Ltd. All Rights Reserved.',
    poweredBy: 'Powered by Stability AI',
    disableMature: true,
  },
  {
    id: 9,
    name: 'Stability AI Community License Agreement',
    url: 'https://stability.ai/community-license-agreement',
    notice:
      'This Stability AI Model is licensed under the Stability AI Community License, Copyright (c)  Stability AI Ltd. All Rights Reserved.',
    poweredBy: 'Powered by Stability AI',
    disableMature: true,
  },
  {
    id: 10,
    name: 'Tencent Hunyuan Community License Agreement',
    url: 'https://github.com/Tencent/HunyuanDiT/blob/main/LICENSE.txt',
    notice:
      'Tencent Hunyuan is licensed under the Tencent Hunyuan Community License Agreement, Copyright © 2024 Tencent. All Rights Reserved. The trademark rights of "Tencent Hunyuan" are owned by Tencent or its affiliate.',
    poweredBy: 'Powered by Tencent Hunyuan',
  },
  {
    id: 11,
    name: 'Tencent Hunyuan Video License Agreement',
    url: 'https://huggingface.co/tencent/HunyuanVideo/blob/main/LICENSE',
    notice:
      'Tencent Hunyuan is licensed under the Tencent Hunyuan Community License Agreement, Copyright © 2024 Tencent. All Rights Reserved. The trademark rights of "Tencent Hunyuan" are owned by Tencent or its affiliate.',
    poweredBy: 'Powered by Tencent Hunyuan',
  },
  {
    id: 12,
    name: 'Kolors License',
    url: 'https://raw.githubusercontent.com/Kwai-Kolors/Kolors/master/MODEL_LICENSE',
    notice:
      'Kolors is licensed under the Kolors Model License Agreement, Copyright (c) Kuaishou Technology. All Rights Reserved.',
  },
  {
    id: 13,
    name: 'Apache 2.0',
    url: 'https://huggingface.co/datasets/choosealicense/licenses/blob/main/markdown/apache-2.0.md',
  },
  {
    id: 14,
    name: 'FLUX.1 [dev] Non-Commercial License',
    url: 'https://huggingface.co/black-forest-labs/FLUX.1-dev/blob/main/LICENSE.md',
    notice:
      'The FLUX.1 [dev] Model is licensed by Black Forest Labs. Inc. under the FLUX.1 [dev] Non-Commercial License. Copyright Black Forest Labs. Inc.',
    poweredBy:
      'IN NO EVENT SHALL BLACK FOREST LABS, INC. BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH USE OF THIS MODEL.',
  },
  {
    id: 15,
    name: 'Illustrious License',
    url: 'https://freedevproject.org/faipl-1.0-sd/',
  },
  {
    id: 16,
    name: 'LTX Video License',
    url: 'https://huggingface.co/Lightricks/LTX-Video/blob/8984fa25007f376c1a299016d0957a37a2f797bb/LTX-Video-Open-Weights-License-0.X.txt',
  },
  {
    id: 17,
    name: 'CogVideoX License',
    url: 'https://huggingface.co/THUDM/CogVideoX-5b/blob/main/LICENSE',
  },
  {
    id: 18,
    name: 'NoobAI License',
    url: 'https://huggingface.co/Laxhar/noobai-XL-1.0/blob/main/README.md#model-license',
  },
  {
    id: 19,
    name: 'MIT',
    url: 'https://huggingface.co/datasets/choosealicense/licenses/blob/main/markdown/mit.md',
  },
  {
    id: 20,
    name: 'OpenAI',
    url: 'https://openai.com/policies/',
  },
  {
    id: 21,
    name: 'Imagen4',
    url: 'https://deepmind.google/about/responsibility-safety/',
  },
  {
    id: 22,
    name: 'Veo 3',
    url: 'https://policies.google.com/terms',
  },
  {
    id: 23,
    name: 'Seedream',
    url: 'https://seed.bytedance.com/en/user-agreement',
  },
  {
    id: 24,
    name: 'Pony',
    url: 'https://purplesmart.ai/license',
  },
  {
    id: 25,
    name: 'CircleStone Labs Non-Commercial License v1.0',
    url: 'https://huggingface.co/circlestone-labs/Anima/blob/main/LICENSE.md',
    notice:
      'The CircleStone Model is licensed by CircleStone Labs LLC under the CircleStone Non-Commercial License. Copyright CircleStone Labs LLC. IN NO EVENT SHALL CIRCLESTONE LABS LLC BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH USE OF THIS MODEL.',
  },
  {
    id: 26,
    name: 'Grok',
    url: 'https://x.ai/legal/terms-of-service',
  },
  {
    id: 27,
    name: 'HappyHorse',
    url: 'https://happyhorsesai.com/',
  },
  {
    id: 28,
    name: 'Krea AI Terms of Service',
    url: 'https://www.krea.ai/terms',
  },
  {
    id: 29,
    name: 'FLUX.2 [dev] Non-Commercial License',
    url: 'https://huggingface.co/black-forest-labs/FLUX.2-dev/blob/main/LICENSE.md',
    notice:
      'The FLUX.2 [dev] Model is licensed by Black Forest Labs. Inc. under the FLUX.2 [dev] Non-Commercial License. Copyright Black Forest Labs. Inc.',
    poweredBy:
      'IN NO EVENT SHALL BLACK FOREST LABS, INC. BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH USE OF THIS MODEL.',
  },
  {
    id: 30,
    name: 'FLUX.2 Klein 9B Non-Commercial License',
    url: 'https://huggingface.co/black-forest-labs/FLUX.2-klein-9B/blob/main/LICENSE.md',
    notice:
      'The FLUX.2 Klein 9B Model is licensed by Black Forest Labs. Inc. under the FLUX Non-Commercial License. Copyright Black Forest Labs. Inc.',
    poweredBy:
      'IN NO EVENT SHALL BLACK FOREST LABS, INC. BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH USE OF THIS MODEL.',
  },
  {
    id: 31,
    name: 'Alibaba Wan / Tongyi Wanxiang Terms of Service',
    url: 'https://wan.video/',
  },
  {
    id: 32,
    name: 'ShengShu Vidu Terms of Service',
    url: 'https://www.vidu.com/terms',
  },
  {
    id: 33,
    name: 'MiniMax Hailuo AI Terms of Service',
    url: 'https://hailuoai.video/doc/terms-of-service.html',
  },
  {
    id: 34,
    name: 'Kuaishou Kling AI Terms of Service',
    url: 'https://klingai.com/docs/user-policy',
  },
  {
    id: 35,
    name: 'LTX-2 Community License Agreement',
    url: 'https://huggingface.co/Lightricks/LTX-2.3/blob/6f3520585aa27248020550da2f453aa0c572398c/LICENSE',
  },
  {
    id: 36,
    name: 'Microsoft AI Terms of Use',
    url: 'https://www.microsoft.com/en-us/servicesagreement',
  },
  {
    id: 37,
    name: 'Ideogram Non-Commercial Model Agreement',
    // Public blob page — the nf4 `raw` URL is gated and 401s, and their license's
    // own cited github URL 404s. The license name links here in the UI.
    url: 'https://huggingface.co/ideogram-ai/ideogram-4-nf4/blob/main/LICENSE.md',
    // Section 3(iii) attribution wording; the URL lives on the linked license name
    // above the notice, so we omit the bare URL here.
    notice:
      'Ideogram 4 is provided under and subject to the Ideogram Non-Commercial Model Agreement. All rights reserved. Copyright © Ideogram, Inc.',
    disableMature: true,
    nonCommercial: true,
  },
  {
    id: 38,
    name: 'Reve AI Terms of Service',
    url: 'https://app.reve.com/terms',
  },
  {
    id: 39,
    name: 'Black Forest Labs Terms of Service',
    url: 'https://bfl.ai/legal/terms-of-service',
  },
  {
    id: 40,
    // Separate from id 35: this is the Aug 2026 rewrite, scoped by its own section 1.9
    // to LTX-2.5 and later, and stricter than the Jan 2026 text the 2.x weights carry.
    name: 'LTX-2.x Community License Agreement',
    url: 'https://github.com/Lightricks/LTX-2/blob/2362161611a61154d342e02724fb8fe58efd455d/LICENSE.md',
  },
  {
    id: 41,
    // Separate from id 33: that is the Hailuo hosted-service ToS, this is the
    // 2 Aug 2026 open-weights licence the on-site H3 models carry.
    name: 'MiniMax H3 Community License Agreement',
    // Permalinked to the 2 Aug 2026 revision. The model page tracks the latest
    // commit, and section III.1 obliges us to hand over a stable copy.
    url: 'https://huggingface.co/MiniMaxAI/MiniMax-H3/blob/42ed227ee7df40d41602854ae760620d6eb651fe/LICENSE',
    notice:
      'MiniMax H3 is licensed by MiniMax under the MiniMax H3 Community License Agreement. That agreement’s Applicable Territory excludes the European Union, the United Kingdom, the Republic of Korea and the United States of America. Your use of H3 and of any H3 derivative is subject to that agreement and its Acceptable Use Policy.',
    // Section IV.2 demands this exact string in the product UI. "Powered by
    // MiniMax H3" is the separate, merely encouraged notice in III.3(a).
    attribution: 'MiniMax H3',
    poweredBy: 'MiniMax H3',
  },
];

export const licenseById = new Map(licenses.map((l) => [l.id, l]));

// =============================================================================
// Ecosystem Families
// =============================================================================

export const ecosystemFamilies: BaseModelFamilyRecord[] = [
  {
    id: 1,
    name: 'Black Forest Labs',
    description: "Black Forest Labs' family of state-of-the-art image generation models",
  },
  {
    id: 2,
    name: 'Stability AI',
    description: "Stability AI's foundational open-source diffusion models",
  },
  {
    id: 3,
    name: 'SDXL Community',
    description: 'Community-trained models built on the SDXL architecture',
  },
  {
    id: 4,
    name: 'Tencent',
    description: "Tencent's family of image and video generation models",
  },
  {
    id: 5,
    name: 'Alibaba',
    description: "Alibaba's video generation model series with various sizes and modes",
  },
  {
    id: 6,
    name: 'PixArt',
    description: 'Efficient transformer-based text-to-image models',
  },
  {
    id: 7,
    name: 'Google',
    description: "Google's image and video generation models",
  },
  {
    id: 8,
    name: 'OpenAI',
    description: "OpenAI's creative image and video generation models",
  },
  {
    id: 9,
    name: 'Pony Diffusion',
    description: 'Community models with extensive tag-based prompt support',
  },
  {
    id: 10,
    name: 'Alibaba',
    description: "Alibaba's multimodal model family with image generation capabilities",
  },
  {
    id: 11,
    name: 'Alibaba - Tongyi Lab',
    description: 'Z Image generation models',
  },
  {
    id: 12,
    name: 'ByteDance',
    description: "ByteDance's image and video generation models",
  },
  {
    id: 13,
    name: 'xAI',
    description: "xAI's image and video generation models",
  },
  {
    id: 14,
    name: 'Kuaishou',
    description: "Kuaishou's video generation models",
  },
  {
    id: 15,
    name: 'ShengShu Technology',
    description: "ShengShu Technology's video generation models",
  },
  {
    id: 16,
    name: 'Lightricks',
    description: "Lightricks' video generation models",
  },
  {
    id: 17,
    name: 'Baidu',
    description: "Baidu's image generation models",
  },
  {
    id: 18,
    name: 'Alibaba - Taotian',
    description: "Alibaba Taotian Future Life Lab's video generation models",
  },
  {
    id: 19,
    name: 'HiDream',
    description: "HiDream.ai's image generation models",
  },
  {
    id: 20,
    name: 'Krea AI',
    description: "Krea AI's in-house image generation models",
  },
  {
    id: 21,
    name: 'Microsoft',
    description: "Microsoft's image generation and editing models",
  },
  {
    id: 22,
    name: 'Ideogram',
    description: "Ideogram, Inc.'s text-to-image generation models with strong typography",
  },
  {
    id: 23,
    name: 'Boogu',
    description: "Boogu's unified multimodal image generation and editing models",
  },
  {
    id: 24,
    name: 'Reve AI',
    description: "Reve AI's controllable 4K text-to-image generation and editing models",
  },
];

export const ecosystemFamilyById = new Map(ecosystemFamilies.map((f) => [f.id, f]));

// NOTE: BaseModelGroups have been removed. Base models now reference ecosystems directly.

// =============================================================================
// Base Models (with ecosystemId)
// =============================================================================

export const baseModelRecords: BaseModelRecord[] = [
  // Anima
  {
    id: BM.Anima,
    name: 'Anima',
    description: 'Image generation model from CircleStone Labs',
    type: 'image',
    ecosystemId: ECO.Anima,
    licenseId: 25,
  },

  // AuraFlow
  {
    id: BM.AuraFlow,
    name: 'AuraFlow',
    description: 'Open-source text-to-image model from Fal.ai with strong prompt adherence',
    type: 'image',
    ecosystemId: ECO.AuraFlow,
    licenseId: 13,
  },

  // Chroma
  {
    id: BM.Chroma,
    name: 'Chroma',
    description: 'Open-source model based on Flux architecture with improved color and composition',
    type: 'image',
    ecosystemId: ECO.Chroma,
    licenseId: 13,
  },

  // CogVideoX
  {
    id: BM.CogVideoX,
    name: 'CogVideoX',
    description: 'Text-to-video diffusion model from Tsinghua University and ZhipuAI',
    type: 'video',
    ecosystemId: ECO.CogVideoX,
    licenseId: 17,
  },

  // Ernie
  {
    id: BM.Ernie,
    name: 'Ernie',
    description: "Baidu's image generation model",
    type: 'image',
    ecosystemId: ECO.Ernie,
    licenseId: 13,
  },

  // Flux.1
  {
    id: BM.Flux1S,
    name: 'Flux.1 S',
    description: 'First generation Flux schnell variant',
    type: 'image',
    ecosystemId: ECO.Flux1,
    licenseId: 13,
  },
  {
    id: BM.Flux1D,
    name: 'Flux.1 D',
    description: 'First generation Flux dev variant',
    type: 'image',
    ecosystemId: ECO.Flux1,
    licenseId: 14,
  },
  {
    id: BM.Flux1Krea,
    name: 'Flux.1 Krea',
    description: 'Krea-trained variant of Flux optimized for creative generation',
    type: 'image',
    ecosystemId: ECO.FluxKrea,
    licenseId: 14,
  },
  {
    id: BM.Flux1Kontext,
    name: 'Flux.1 Kontext',
    description: 'Flux variant specialized for context-aware image editing and generation',
    type: 'image',
    ecosystemId: ECO.Flux1Kontext,
    licenseId: 14,
  },
  {
    id: BM.Flux2D,
    name: 'Flux.2 D',
    description: 'Next-generation Flux with enhanced capabilities',
    type: 'image',
    ecosystemId: ECO.Flux2,
    licenseId: 29,
  },
  {
    id: BM.Flux2Klein_9B,
    name: 'Flux.2 Klein 9B',
    description: 'Flux.2 Klein 9 billion parameter distilled model',
    type: 'image',
    ecosystemId: ECO.Flux2Klein_9B,
    licenseId: 30,
  },
  {
    id: BM.Flux2Klein_9B_base,
    name: 'Flux.2 Klein 9B-base',
    description: 'Flux.2 Klein 9 billion parameter base model',
    type: 'image',
    ecosystemId: ECO.Flux2Klein_9B_base,
    licenseId: 30,
  },
  {
    id: BM.Flux2Klein_4B,
    name: 'Flux.2 Klein 4B',
    description: 'Flux.2 Klein 4 billion parameter distilled model',
    type: 'image',
    ecosystemId: ECO.Flux2Klein_4B,
    licenseId: 13,
  },
  {
    id: BM.Flux2Klein_4B_base,
    name: 'Flux.2 Klein 4B-base',
    description: 'Flux.2 Klein 4 billion parameter base model',
    type: 'image',
    ecosystemId: ECO.Flux2Klein_4B_base,
    licenseId: 13,
  },
  {
    id: BM.Flux3Video,
    name: 'Flux 3 Video',
    description: "Black Forest Labs' FLUX 3 video generation model with native audio",
    type: 'video',
    ecosystemId: ECO.Flux3Video,
    licenseId: 39,
  },

  // Grok
  {
    id: BM.Grok,
    name: 'Grok',
    description: 'Image and video generation model from xAI',
    type: ['image', 'video'],
    ecosystemId: ECO.Grok,
    licenseId: 26,
  },

  // HappyHorse
  {
    id: BM.HappyHorse,
    name: 'HappyHorse',
    description:
      "Alibaba's video generation model with synchronized audio, supporting text-to-video and image-to-video",
    type: 'video',
    ecosystemId: ECO.HappyHorse,
    licenseId: 27,
  },

  // HiDream
  {
    id: BM.HiDream,
    name: 'HiDream',
    description: 'High-resolution image generation model optimized for detailed outputs',
    type: 'image',
    ecosystemId: ECO.HiDream,
    licenseId: 19,
  },
  {
    id: BM.HiDreamO1,
    name: 'HiDream-O1',
    description: "HiDream.ai's unified pixel-level transformer for text-to-image generation",
    type: 'image',
    ecosystemId: ECO.HiDreamO1,
    licenseId: 19,
  },

  // Hunyuan
  {
    id: BM.Hunyuan1,
    name: 'Hunyuan 1',
    description: 'Diffusion transformer for bilingual Chinese-English image generation',
    type: 'image',
    ecosystemId: ECO.HyDit1,
    licenseId: 10,
  },
  {
    id: BM.HunyuanVideo,
    name: 'Hunyuan Video',
    description: 'Video generation model with strong motion coherence',
    type: 'video',
    ecosystemId: ECO.HyV1,
    licenseId: 11,
  },

  // Ideogram
  {
    id: BM.Ideogram,
    name: 'Ideogram 4.0',
    description: "Ideogram, Inc.'s text-to-image generation model with strong typography",
    type: 'image',
    ecosystemId: ECO.Ideogram,
    licenseId: 37,
  },

  // Boogu
  {
    id: BM.Boogu,
    name: 'Boogu',
    description: "Boogu's unified multimodal image generation and editing model",
    type: 'image',
    ecosystemId: ECO.Boogu,
    licenseId: 13,
  },

  // Illustrious
  {
    id: BM.Illustrious,
    name: 'Illustrious',
    description: 'SDXL-based model specialized for anime and illustration styles',
    type: 'image',
    ecosystemId: ECO.Illustrious,
    licenseId: 15,
  },

  // Imagen4
  {
    id: BM.Imagen4,
    name: 'Imagen4',
    description: 'Text-to-image model with photorealistic capabilities',
    type: 'image',
    ecosystemId: ECO.Imagen4,
    hidden: true,
    licenseId: 21,
  },

  // Kolors
  {
    id: BM.Kolors,
    name: 'Kolors',
    description: "Kuaishou's bilingual image generation model with vibrant color output",
    type: 'image',
    ecosystemId: ECO.Kolors,
    licenseId: 12,
  },

  // Krea 2
  {
    id: BM.Krea2,
    name: 'Krea 2',
    description: "Krea AI's in-house image generation model",
    type: 'image',
    ecosystemId: ECO.Krea2,
    licenseId: 28,
  },

  // LTXV
  {
    id: BM.LTXV,
    name: 'LTXV',
    description: "Lightricks' efficient video generation model for fast rendering",
    type: 'video',
    ecosystemId: ECO.LTXV,
    licenseId: 16,
  },
  {
    id: BM.LTXV2,
    name: 'LTXV2',
    description: "Lightricks' next-generation video generation model",
    type: 'video',
    ecosystemId: ECO.LTXV2,
    licenseId: 35,
  },
  {
    id: BM.LTXV23,
    name: 'LTXV 2.3',
    description: "Lightricks' LTX Video 2.3 generation model",
    type: 'video',
    ecosystemId: ECO.LTXV23,
    licenseId: 35,
  },
  {
    id: BM.LTXV25,
    name: 'LTXV 2.5',
    description: "Lightricks' LTX Video 2.5 generation model",
    type: 'video',
    ecosystemId: ECO.LTXV25,
    licenseId: 40,
  },

  // Lens
  {
    id: BM.Lens,
    name: 'Lens',
    description: "Civitai's image generation model",
    type: 'image',
    ecosystemId: ECO.Lens,
  },

  // Lumina
  {
    id: BM.Lumina,
    name: 'Lumina',
    description: 'Open-source model with strong foundations',
    type: 'image',
    ecosystemId: ECO.Lumina,
    licenseId: 13,
  },

  // MageFlow
  {
    id: BM.MageFlow,
    name: 'MageFlow',
    description: "Microsoft's native-resolution image generation and editing model",
    type: 'image',
    ecosystemId: ECO.MageFlow,
    licenseId: 19,
  },

  // MAI
  {
    id: BM.MAI,
    name: 'MAI',
    description: "Microsoft's photorealistic text-to-image and image-editing model",
    type: 'image',
    ecosystemId: ECO.MAI,
    licenseId: 36,
  },

  // Mochi
  {
    id: BM.Mochi,
    name: 'Mochi',
    description: "Genmo's video generation model with realistic motion synthesis",
    type: 'video',
    ecosystemId: ECO.Mochi,
    licenseId: 13,
  },

  // Nano Banana
  {
    id: BM.NanoBanana,
    name: 'Nano Banana',
    description: 'Experimental image generation model',
    type: 'image',
    ecosystemId: ECO.NanoBanana,
    hidden: true,
    licenseId: 21,
  },

  // NoobAI
  {
    id: BM.NoobAI,
    name: 'NoobAI',
    description: 'SDXL-based model trained for anime and stylized content',
    type: 'image',
    ecosystemId: ECO.NoobAI,
    licenseId: 18,
  },

  // ODOR
  {
    id: BM.ODOR,
    name: 'ODOR',
    description: 'Experimental diffusion model architecture',
    type: 'image',
    ecosystemId: ECO.ODOR,
    hidden: true,
  },

  // OpenAI
  {
    id: BM.OpenAI,
    name: 'OpenAI',
    description: 'Image generation models including DALL-E',
    type: 'image',
    ecosystemId: ECO.OpenAI,
    hidden: true,
    licenseId: 20,
  },

  // Upscaler
  {
    id: BM.Upscaler,
    name: 'Upscaler',
    description: 'Image upscaling models for increasing resolution',
    type: 'image',
    ecosystemId: ECO.Upscaler,
  },

  // Other
  {
    id: BM.Other,
    name: 'Other',
    description: "Models that don't fit into standard categories",
    type: 'image',
    ecosystemId: ECO.Other,
  },

  // PixArt
  {
    id: BM.PixArtA,
    name: 'PixArt a',
    description: 'Efficient transformer-based model with fast training and strong quality',
    type: 'image',
    ecosystemId: ECO.PixArtA,
    licenseId: 3,
  },
  {
    id: BM.PixArtE,
    name: 'PixArt E',
    description: 'Enhanced PixArt with 4K resolution support and improved detail',
    type: 'image',
    ecosystemId: ECO.PixArtE,
    licenseId: 3,
  },

  // Playground v2
  {
    id: BM.PlaygroundV2,
    name: 'Playground v2',
    description: "Playground AI's model optimized for aesthetic image generation",
    type: 'image',
    ecosystemId: ECO.PlaygroundV2,
    hidden: true,
    licenseId: 6,
  },

  // Pony
  {
    id: BM.Pony,
    name: 'Pony',
    description: 'SDXL-based model with extensive tag-based prompt support',
    type: 'image',
    ecosystemId: ECO.Pony,
    licenseId: 1,
  },
  {
    id: BM.PonyV7,
    name: 'Pony V7',
    description: 'Latest Pony Diffusion built on AuraFlow architecture',
    type: 'image',
    ecosystemId: ECO.PonyV7,
    licenseId: 24,
  },

  // Qwen
  {
    id: BM.Qwen,
    name: 'Qwen',
    description: 'Multimodal model with image generation capabilities',
    type: 'image',
    ecosystemId: ECO.Qwen,
    licenseId: 13,
    experimental: true,
  },
  {
    id: BM.Qwen2,
    name: 'Qwen 2',
    description: 'Next-generation Qwen image generation model',
    type: 'image',
    ecosystemId: ECO.Qwen2,
    licenseId: 13,
  },
  {
    id: BM.Qwen3,
    name: 'Qwen 3',
    description: "Alibaba's Qwen 3 image generation model",
    type: 'image',
    ecosystemId: ECO.Qwen3,
    licenseId: 13,
  },

  // Stable Cascade
  {
    id: BM.StableCascade,
    name: 'Stable Cascade',
    description: 'Cascaded latent diffusion model for high-resolution output',
    type: 'image',
    ecosystemId: ECO.SCascade,
    hidden: true,
    licenseId: 8,
  },

  // SD 1.x
  {
    id: BM.SD14,
    name: 'SD 1.4',
    description: 'The original Stable Diffusion with broad community support',
    type: 'image',
    ecosystemId: ECO.SD1,
    licenseId: 1,
  },
  {
    id: BM.SD15,
    name: 'SD 1.5',
    description: 'The original Stable Diffusion with broad community support',
    type: 'image',
    ecosystemId: ECO.SD1,
    licenseId: 1,
  },
  {
    id: BM.SD15LCM,
    name: 'SD 1.5 LCM',
    description: 'SD 1.5 with Latent Consistency Model for faster inference',
    type: 'image',
    ecosystemId: ECO.SD1,
    licenseId: 1,
  },
  {
    id: BM.SD15Hyper,
    name: 'SD 1.5 Hyper',
    description: 'SD 1.5 with Hyper optimization for reduced steps',
    type: 'image',
    ecosystemId: ECO.SD1,
    licenseId: 1,
  },

  // SD 2.x
  {
    id: BM.SD20,
    name: 'SD 2.0',
    description: 'Second generation SD with improved architecture',
    type: 'image',
    ecosystemId: ECO.SD2,
    licenseId: 3,
  },
  {
    id: BM.SD20_768,
    name: 'SD 2.0 768',
    description: 'SD 2.0 with 768px support',
    type: 'image',
    ecosystemId: ECO.SD2,
    hidden: true,
    licenseId: 3,
  },
  {
    id: BM.SD21,
    name: 'SD 2.1',
    description: 'Second generation SD with improved architecture and 768px support',
    type: 'image',
    ecosystemId: ECO.SD2,
    licenseId: 3,
  },
  {
    id: BM.SD21_768,
    name: 'SD 2.1 768',
    description: 'SD 2.1 with 768px support',
    type: 'image',
    ecosystemId: ECO.SD2,
    hidden: true,
    licenseId: 3,
  },
  {
    id: BM.SD21Unclip,
    name: 'SD 2.1 Unclip',
    description: 'SD 2.1 variant with image-to-image capabilities',
    type: 'image',
    ecosystemId: ECO.SD2,
    hidden: true,
    licenseId: 3,
  },

  // SD 3.x - fully disabled (no support for any type)
  {
    id: BM.SD3,
    name: 'SD 3',
    description: 'Multimodal diffusion transformer architecture',
    type: 'image',
    ecosystemId: ECO.SD3,
    hidden: true,
    disabled: true,
    experimental: true,
    licenseId: 9,
  },
  {
    id: BM.SD35,
    name: 'SD 3.5',
    description: 'Multimodal diffusion transformer architecture',
    type: 'image',
    ecosystemId: ECO.SD3,
    hidden: true,
    disabled: true,
    experimental: true,
    licenseId: 9,
  },
  {
    id: BM.SD35Large,
    name: 'SD 3.5 Large',
    description: 'Multimodal diffusion transformer architecture',
    type: 'image',
    ecosystemId: ECO.SD3,
    hidden: true,
    disabled: true,
    experimental: true,
    licenseId: 9,
  },
  {
    id: BM.SD35LargeTurbo,
    name: 'SD 3.5 Large Turbo',
    description: 'Multimodal diffusion transformer architecture with turbo optimization',
    type: 'image',
    ecosystemId: ECO.SD3,
    hidden: true,
    disabled: true,
    experimental: true,
    licenseId: 9,
  },
  {
    id: BM.SD35Medium,
    name: 'SD 3.5 Medium',
    description: 'Balanced SD3.5 variant optimized for quality and speed',
    type: 'image',
    ecosystemId: ECO.SD35M,
    hidden: true,
    disabled: true,
    experimental: true,
    licenseId: 9,
  },

  // SDXL
  {
    id: BM.SDXL09,
    name: 'SDXL 0.9',
    description: 'High-resolution SD with improved prompt understanding and detail',
    type: 'image',
    ecosystemId: ECO.SDXL,
    hidden: true,
    licenseId: 2,
  },
  {
    id: BM.SDXL10,
    name: 'SDXL 1.0',
    description: 'High-resolution SD with improved prompt understanding and detail',
    type: 'image',
    ecosystemId: ECO.SDXL,
    licenseId: 3,
  },
  {
    id: BM.SDXL10LCM,
    name: 'SDXL 1.0 LCM',
    description: 'SDXL with Latent Consistency Model for faster inference',
    type: 'image',
    ecosystemId: ECO.SDXL,
    hidden: true,
    licenseId: 3,
  },
  {
    id: BM.SDXLLightning,
    name: 'SDXL Lightning',
    description: 'SDXL with Lightning optimization for reduced steps',
    type: 'image',
    ecosystemId: ECO.SDXL,
    licenseId: 3,
  },
  {
    id: BM.SDXLHyper,
    name: 'SDXL Hyper',
    description: 'SDXL with Hyper optimization for reduced steps',
    type: 'image',
    ecosystemId: ECO.SDXL,
    licenseId: 3,
  },
  {
    id: BM.SDXLTurbo,
    name: 'SDXL Turbo',
    description: 'SDXL with Turbo optimization for minimal steps',
    type: 'image',
    ecosystemId: ECO.SDXL,
    hidden: true,
    disabled: true,
    licenseId: 4,
  },
  {
    id: BM.SDXLDistilled,
    name: 'SDXL Distilled',
    description: 'Faster SDXL variants with reduced inference steps',
    type: 'image',
    ecosystemId: ECO.SDXLDistilled,
    hidden: true,
    licenseId: 3,
  },

  // Reve
  {
    id: BM.Reve,
    name: 'Reve',
    description: "Reve AI's controllable 4K text-to-image generation and editing model",
    type: 'image',
    ecosystemId: ECO.Reve,
    licenseId: 38,
  },

  // Seedream
  {
    id: BM.Seedream,
    name: 'Seedream',
    description: "ByteDance's image generation model",
    type: 'image',
    ecosystemId: ECO.Seedream,
    hidden: true,
    licenseId: 23,
  },

  // SVD - fully disabled (no support for any type)
  {
    id: BM.SVD,
    name: 'SVD',
    description: 'Image-to-video diffusion model',
    type: 'image',
    ecosystemId: ECO.SVD,
    hidden: true,
    disabled: true,
    licenseId: 5,
  },
  {
    id: BM.SVDXT,
    name: 'SVD XT',
    description: 'Extended SVD for longer video generation',
    type: 'image',
    ecosystemId: ECO.SVD,
    hidden: true,
    disabled: true,
    licenseId: 5,
  },

  // Sora 2
  {
    id: BM.Sora2,
    name: 'Sora 2',
    description: 'Advanced video generation model',
    type: 'video',
    ecosystemId: ECO.Sora2,
    hidden: true,
    licenseId: 20,
  },

  // Veo 3
  {
    id: BM.Veo3,
    name: 'Veo 3',
    description: 'Latest video generation model from DeepMind',
    type: 'video',
    ecosystemId: ECO.Veo3,
    hidden: true,
    licenseId: 22,
  },

  // Wan Video
  {
    id: BM.WanVideo,
    name: 'Wan Video',
    description: 'Base video generation model',
    type: 'video',
    ecosystemId: ECO.WanVideo,
    hidden: true,
    licenseId: 13,
  },
  {
    id: BM.WanVideo13BT2V,
    name: 'Wan Video 1.3B t2v',
    description: 'Lightweight text-to-video model',
    type: 'video',
    ecosystemId: ECO.WanVideo1_3B_T2V,
    licenseId: 13,
  },
  {
    id: BM.WanVideo14BT2V,
    name: 'Wan Video 14B t2v',
    description: 'Full-scale text-to-video model',
    type: 'video',
    ecosystemId: ECO.WanVideo14B_T2V,
    licenseId: 13,
  },
  {
    id: BM.WanVideo14BI2V480p,
    name: 'Wan Video 14B i2v 480p',
    description: 'Image-to-video at 480p resolution',
    type: 'video',
    ecosystemId: ECO.WanVideo14B_I2V_480p,
    licenseId: 13,
  },
  {
    id: BM.WanVideo14BI2V720p,
    name: 'Wan Video 14B i2v 720p',
    description: 'Image-to-video at 720p resolution',
    type: 'video',
    ecosystemId: ECO.WanVideo14B_I2V_720p,
    licenseId: 13,
  },
  {
    id: BM.WanVideo22TI2V5B,
    name: 'Wan Video 2.2 TI2V-5B',
    description: 'Text/image-to-video 5B parameter model',
    type: 'video',
    ecosystemId: ECO.WanVideo22_TI2V_5B,
    licenseId: 13,
  },
  {
    id: BM.WanVideo22I2VA14B,
    name: 'Wan Video 2.2 I2V-A14B',
    description: 'Image-to-video 14B parameter model',
    type: 'video',
    ecosystemId: ECO.WanVideo22_I2V_A14B,
    licenseId: 13,
  },
  {
    id: BM.WanVideo22T2VA14B,
    name: 'Wan Video 2.2 T2V-A14B',
    description: 'Text-to-video 14B parameter model',
    type: 'video',
    ecosystemId: ECO.WanVideo22_T2V_A14B,
    licenseId: 13,
  },
  {
    id: BM.WanVideo25T2V,
    name: 'Wan Video 2.5 T2V',
    description: 'Latest text-to-video generation',
    type: 'video',
    ecosystemId: ECO.WanVideo25_T2V,
    licenseId: 31,
  },
  {
    id: BM.WanVideo25I2V,
    name: 'Wan Video 2.5 I2V',
    description: 'Latest image-to-video generation',
    type: 'video',
    ecosystemId: ECO.WanVideo25_I2V,
    licenseId: 31,
  },
  {
    id: BM.WanImage27,
    name: 'Wan Image 2.7',
    description: 'Image generation model from Alibaba',
    type: 'image',
    ecosystemId: ECO.WanImage27,
    licenseId: 31,
  },
  {
    id: BM.WanVideo27,
    name: 'Wan Video 2.7',
    description: 'Video generation model from Alibaba',
    type: 'video',
    ecosystemId: ECO.WanVideo27,
    licenseId: 31,
  },
  {
    id: BM.WanVideo30,
    name: 'Wan Video 3.0',
    description: 'Video generation model from Alibaba',
    type: 'video',
    ecosystemId: ECO.WanVideo30,
    licenseId: 31,
  },

  // ZImageTurbo
  {
    id: BM.ZImageTurbo,
    name: 'ZImageTurbo',
    description: 'Fast turbo-optimized image generation model',
    type: 'image',
    ecosystemId: ECO.ZImageTurbo,
    licenseId: 13,
  },

  // ZImageBase
  {
    id: BM.ZImageBase,
    name: 'ZImageBase',
    description: 'Base image generation model',
    type: 'image',
    ecosystemId: ECO.ZImageBase,
    licenseId: 13,
  },

  // Vidu Q1
  {
    id: BM.Vidu,
    name: 'Vidu Q1',
    description: 'High-quality video generation model from Vidu',
    type: 'video',
    ecosystemId: ECO.Vidu,
    hidden: true,
    licenseId: 32,
  },

  // Hailuo H3 by MiniMax
  {
    id: BM.MiniMaxH3,
    name: 'MiniMax H3',
    description: "MiniMax's video generation model with cinematic quality",
    type: 'video',
    ecosystemId: ECO.MiniMaxH3,
    licenseId: 41,
  },

  // Kling
  {
    id: BM.Kling,
    name: 'Kling',
    description: "Kuaishou's video generation model",
    type: 'video',
    ecosystemId: ECO.Kling,
    hidden: true,
    licenseId: 34,
  },

  // Seedance
  {
    id: BM.Seedance,
    name: 'Seedance',
    description: "ByteDance's video generation model",
    type: 'video',
    ecosystemId: ECO.Seedance,
    hidden: true,
    licenseId: 23,
  },

  // ACE Audio
  {
    id: BM.AceAudio,
    name: 'ACE Audio',
    description: 'ACE Audio music generation model',
    type: 'audio',
    ecosystemId: ECO.AceAudio,
    licenseId: 13,
  },

  // PolyGen (Meshy via Fal) — remote 3D model generator. Type='image' matches
  // the Upscaler base-model convention for "no Civitai checkpoint" ecosystems
  // (Prisma's MediaType enum has no 'model3d' variant). Hidden from the
  // base-model picker — PolyGen exposes no Civitai resources.
  {
    id: BM.PolyGen,
    name: 'PolyGen',
    description: 'Meshy text-to-3D / image-to-3D generation (via Fal)',
    type: 'image',
    ecosystemId: ECO.PolyGen,
    hidden: true,
  },
  // Tripo / Hunyuan3D — remote image-to-3D generators. Same `type: 'image'`,
  // resource-less, hidden-from-picker convention as PolyGen above.
  {
    id: BM.Tripo,
    name: 'Tripo',
    description: 'Tripo image-to-3D generation (via Fal)',
    type: 'image',
    ecosystemId: ECO.Tripo,
    hidden: true,
  },
  {
    id: BM.Hunyuan3D,
    name: 'Hunyuan3D',
    description: 'Hunyuan3D image-to-3D generation (via Comfy)',
    type: 'image',
    ecosystemId: ECO.Hunyuan3D,
    hidden: true,
  },
];

export const baseModelById = new Map(baseModelRecords.map((m) => [m.id, m]));
export const baseModelByName = new Map(baseModelRecords.map((m) => [m.name, m]));

/** Grouped select data for base model multi-select inputs, grouped by ecosystem family */
export const baseModelSelectData = (() => {
  const activeModels = baseModelRecords.filter((m) => !m.hidden);
  const groupMap = new Map<string, { value: string; label: string }[]>();

  for (const model of activeModels) {
    const ecosystem = ecosystemById.get(model.ecosystemId);
    const family = ecosystem?.familyId ? ecosystemFamilyById.get(ecosystem.familyId) : undefined;
    const groupName = family?.name ?? ecosystem?.displayName ?? model.name;

    if (!groupMap.has(groupName)) groupMap.set(groupName, []);
    groupMap.get(groupName)!.push({ value: model.name, label: model.name });
  }

  return Array.from(groupMap.entries()).map(([group, items]) => ({ group, items }));
})();

export function getEcosystem(baseModel: string) {
  const model = baseModelByName.get(baseModel);
  if (model) return ecosystemById.get(model.ecosystemId);
}

const fileSizeEcosystemKeys: readonly string[] = ['SD1'];

/**
 * Whether a base model still distinguishes full vs pruned checkpoint weights.
 * Retired everywhere except SD 1.x, which is where nearly every real
 * full/pruned file pair lives — SD 2.x and later are a negligible long tail.
 */
export function baseModelHasFileSize(baseModel?: string | null): boolean {
  if (!baseModel) return false;
  const ecosystem = getEcosystem(baseModel);
  return !!ecosystem && fileSizeEcosystemKeys.includes(ecosystem.key);
}

/**
 * Check if any base model in an ecosystem is marked as experimental
 * @param ecosystemKey - The ecosystem key (e.g., 'SD3', 'Qwen')
 * @returns true if any base model in the ecosystem is experimental
 */
export function isEcosystemExperimental(ecosystemKey: string): boolean {
  const ecosystem = ecosystemByKey.get(ecosystemKey);
  if (!ecosystem) return false;

  // Check if any base model in this ecosystem is experimental
  return baseModelRecords.some((m) => m.ecosystemId === ecosystem.id && m.experimental);
}

// =============================================================================
// Derivation Functions
// =============================================================================

/**
 * Get the root ecosystem for an ecosystem (follows parent chain)
 */
export function getRootEcosystem(ecosystemIdOrBaseModel: number | string): EcosystemRecord {
  const ecosystem =
    typeof ecosystemIdOrBaseModel === 'string'
      ? getEcosystem(ecosystemIdOrBaseModel) ?? ecosystemByKey.get(ecosystemIdOrBaseModel)
      : ecosystemById.get(ecosystemIdOrBaseModel);
  if (!ecosystem) throw new Error(`Ecosystem ${ecosystemIdOrBaseModel} not found`);

  if (ecosystem.parentEcosystemId) {
    return getRootEcosystem(ecosystem.parentEcosystemId);
  }
  return ecosystem;
}

/**
 * Clip skip is a CLIP text-encoder concept that only applies to the Stable
 * Diffusion 1.x and SDXL families (SDXL children like Pony, Illustrious and
 * NoobAI resolve to the SDXL root). Returns false for everything else
 * (Flux, SD3, video ecosystems, etc.) and for unknown base models.
 */
export function baseModelSupportsClipSkip(baseModel?: string | null): boolean {
  if (!baseModel) return false;
  try {
    const root = getRootEcosystem(baseModel);
    return root.id === ECO.SD1 || root.id === ECO.SDXL;
  } catch {
    return false;
  }
}

/**
 * Get ecosystem support, with inheritance from parent
 */
export function getEcosystemSupport(
  ecosystemId: number,
  supportType: SupportType
): EcosystemSupport | undefined {
  // Check for explicit entry
  const explicit = ecosystemSupport.find(
    (s) => s.ecosystemId === ecosystemId && s.supportType === supportType
  );
  if (explicit) return explicit;

  // Check parent ecosystem
  const ecosystem = ecosystemById.get(ecosystemId);
  if (ecosystem?.parentEcosystemId) {
    return getEcosystemSupport(ecosystem.parentEcosystemId, supportType);
  }

  return undefined;
}

/**
 * Get ecosystem setting, with inheritance from parent
 */
export function getEcosystemSetting<K extends keyof NonNullable<EcosystemSettings['defaults']>>(
  ecosystemId: number,
  key: K
): NonNullable<EcosystemSettings['defaults']>[K] | undefined {
  const settings = ecosystemSettings.find((s) => s.ecosystemId === ecosystemId);

  if (settings?.defaults?.[key] !== undefined) {
    return settings.defaults[key];
  }

  // Inherit from parent
  const ecosystem = ecosystemById.get(ecosystemId);
  if (ecosystem?.parentEcosystemId) {
    return getEcosystemSetting(ecosystem.parentEcosystemId, key);
  }

  return undefined;
}

/**
 * All unique default model version IDs across every ecosystem that has one.
 * Used to prefetch resource data for the compatibility confirm modal.
 */
export const allEcosystemDefaultVersionIds: number[] = [
  ...new Set(
    ecosystemSettings.flatMap((s) => (s.defaults?.model?.id ? [s.defaults.model.id] : []))
  ),
];

/**
 * Check if a model is supported for a given support type
 */
export function isModelSupported(
  baseModelId: number,
  supportType: SupportType,
  modelType?: ModelType
): boolean {
  const baseModel = baseModelById.get(baseModelId);
  if (!baseModel) return false;

  // Check if model is entirely disabled (root-level disable)
  if (baseModel.disabled) return false;

  // Check model-level override
  const modelOverride = supportOverrides.find(
    (o) => o.baseModelId === baseModelId && o.supportType === supportType
  );
  if (modelOverride !== undefined) {
    if (modelOverride.disabled) return false;
    if (modelType && modelOverride.modelTypes && !modelOverride.modelTypes.includes(modelType)) {
      return false;
    }
  }

  // Check ecosystem-level override
  const ecosystemOverride = supportOverrides.find(
    (o) =>
      o.ecosystemId === baseModel.ecosystemId && !o.baseModelId && o.supportType === supportType
  );
  if (ecosystemOverride !== undefined) {
    if (ecosystemOverride.disabled) return false;
  }

  // Check ecosystem support
  const support = getEcosystemSupport(baseModel.ecosystemId, supportType);
  if (!support || support.disabled) return false;

  if (modelType && !support.modelTypes.includes(modelType)) {
    return false;
  }

  return true;
}

/**
 * Check if a base model or ecosystem supports generation for a given model type.
 * Accepts either a base model name (e.g., 'SDXL 1.0') or an ecosystem key (e.g., 'SDXL').
 */
export function isBaseModelGenerationSupported(
  baseModelOrEcosystem: string,
  modelType: ModelType
): boolean {
  // Try base model name first
  const model = baseModelByName.get(baseModelOrEcosystem);
  if (model) return isModelSupported(model.id, 'generation', modelType);

  // Fall back to ecosystem key
  const ecosystem = ecosystemByKey.get(baseModelOrEcosystem);
  if (!ecosystem) return false;

  const support = getEcosystemSupport(ecosystem.id, 'generation');
  if (!support || support.disabled) return false;

  return support.modelTypes.includes(modelType);
}

/**
 * Check if a base model or ecosystem has any generation support at all.
 * Accepts either a base model name (e.g., 'SDXL 1.0') or an ecosystem key (e.g., 'SDXL').
 * Unlike isBaseModelGenerationSupported, this does not require a specific modelType.
 */
export function hasGenerationSupport(baseModelOrEcosystem: string): boolean {
  // Try base model name first
  const model = baseModelByName.get(baseModelOrEcosystem);
  if (model) return isModelSupported(model.id, 'generation');

  // Fall back to ecosystem key
  const ecosystem = ecosystemByKey.get(baseModelOrEcosystem);
  if (!ecosystem) return false;

  const support = getEcosystemSupport(ecosystem.id, 'generation');
  return !!support && !support.disabled;
}

/**
 * Check if a resource's base model is compatible with a primary ecosystem for generation.
 * Accepts base model names or ecosystem keys for both the primary and resource parameters.
 *
 * @param primaryBaseModelOrEcosystem - The checkpoint's base model name or ecosystem key
 * @param resourceBaseModel - The resource's base model name
 * @param resourceModelType - The resource's model type (e.g., 'LORA', 'Checkpoint')
 * @returns The support level ('full' | 'partial') or null if not compatible
 */
export function getResourceGenerationSupport(
  primaryBaseModelOrEcosystem: string,
  resourceBaseModel: string,
  resourceModelType: ModelType
): SupportLevel | null {
  // Resolve primary ecosystem
  const primaryModel = baseModelByName.get(primaryBaseModelOrEcosystem);
  const primaryEcosystem = primaryModel
    ? ecosystemById.get(primaryModel.ecosystemId)
    : ecosystemByKey.get(primaryBaseModelOrEcosystem);
  if (!primaryEcosystem) return null;

  // Resolve resource ecosystem
  const resourceModel = baseModelByName.get(resourceBaseModel);
  if (!resourceModel) return null;

  return getGenerationSupport(primaryEcosystem.id, resourceModel.ecosystemId, resourceModelType);
}

/**
 * Get generation support level between two ecosystems
 */
export function getGenerationSupport(
  checkpointEcosystemId: number,
  addonEcosystemId: number,
  addonModelType: ModelType
): SupportLevel | null {
  const checkpointEcosystem = ecosystemById.get(checkpointEcosystemId);
  const addonEcosystem = ecosystemById.get(addonEcosystemId);

  if (!checkpointEcosystem || !addonEcosystem) return null;

  // Same ecosystem = always Full (same-ecosystem resources are inherently compatible)
  if (checkpointEcosystemId === addonEcosystemId) return 'full';

  // Check if generation is supported at all
  const support = getEcosystemSupport(checkpointEcosystemId, 'generation');
  if (!support || support.disabled) return null;

  // Cross-ecosystem compatibility is driven entirely by explicit rules below.
  // The parent-chain relationship is NOT used to infer compatibility because it
  // is also used for identity concerns (AIR URN ecosystem, classification) —
  // e.g. Flux2Klein variants list Flux2 as parent so their AIRs emit `flux2`,
  // but LoRAs trained for one Klein variant are NOT compatible with others.

  // Check cross-ecosystem rules — these define their own modelTypes so they bypass
  // the ecosystem's primary modelTypes list (e.g. SD1 TextualInversion → SDXL family)
  const crossRule = crossEcosystemRules.find(
    (r) =>
      r.sourceEcosystemId === addonEcosystemId &&
      r.targetEcosystemId === checkpointEcosystemId &&
      r.supportType === 'generation' &&
      (!r.modelTypes || r.modelTypes.includes(addonModelType))
  );

  if (crossRule) return crossRule.support;

  // Also check if the target is a child of the rule's target
  const targetRoot = getRootEcosystem(checkpointEcosystemId);
  if (targetRoot.id !== checkpointEcosystemId) {
    const matchingRule = crossEcosystemRules.find(
      (r) =>
        r.sourceEcosystemId === addonEcosystemId &&
        r.targetEcosystemId === targetRoot.id &&
        r.supportType === 'generation' &&
        (!r.modelTypes || r.modelTypes.includes(addonModelType))
    );
    if (matchingRule) return matchingRule.support;
  }

  return null;
}

/**
 * Check if a single resource (by baseModel name and model type) is compatible with an ecosystem.
 * Returns the support level or null if incompatible.
 */
export function getResourceEcosystemCompatibility(
  ecosystemId: number,
  resourceBaseModel: string,
  resourceModelType: ModelType
): SupportLevel | null {
  const bm = baseModelByName.get(resourceBaseModel);
  if (!bm) return null;
  return getGenerationSupport(ecosystemId, bm.ecosystemId, resourceModelType);
}

/** Minimal shape required for ecosystem compatibility checks */
interface ResourceLikeForCompat {
  baseModel?: string;
  model: { type: string };
}

/**
 * Check if ALL resources are compatible with a given ecosystem.
 */
export function areResourcesCompatible(
  ecosystemId: number,
  resources: ResourceLikeForCompat[]
): boolean {
  return resources.every((r) => {
    if (!r.baseModel) return true;
    const bm = baseModelByName.get(r.baseModel);
    if (!bm) return true;
    return getGenerationSupport(ecosystemId, bm.ecosystemId, r.model.type as ModelType) !== null;
  });
}

/**
 * Filter resources to keep only those compatible with a given ecosystem.
 * Optionally exclude specific resource IDs (e.g. incoming replacements).
 */
export function filterCompatibleResources<T extends ResourceLikeForCompat & { id: number }>(
  ecosystemId: number,
  resources: T[],
  excludeIds?: Set<number>
): T[] {
  return resources.filter((r) => {
    if (excludeIds?.has(r.id)) return false;
    if (!r.baseModel) return true;
    const bm = baseModelByName.get(r.baseModel);
    if (!bm) return true;
    return getGenerationSupport(ecosystemId, bm.ecosystemId, r.model.type as ModelType) !== null;
  });
}

/**
 * Get the ecosystem for a base model by ID
 */
export function getBaseModelEcosystemById(baseModelId: number): EcosystemRecord | undefined {
  const model = baseModelById.get(baseModelId);
  if (!model) return undefined;

  return ecosystemById.get(model.ecosystemId);
}

/**
 * Get base models for an ecosystem (including children)
 */
export function getBaseModelsByEcosystemId(
  ecosystemId: number,
  includeChildren = true
): BaseModelRecord[] {
  const ecosystemIds = new Set([ecosystemId]);

  if (includeChildren) {
    // Find all child ecosystems
    for (const eco of ecosystems) {
      if (eco.parentEcosystemId === ecosystemId) {
        ecosystemIds.add(eco.id);
      }
    }
  }

  // Find all base models in these ecosystems
  return baseModelRecords.filter((m) => ecosystemIds.has(m.ecosystemId));
}

/**
 * Get license for a base model
 */
export function getBaseModelLicense(baseModelId: number): LicenseRecord | undefined {
  const model = baseModelById.get(baseModelId);
  if (!model?.licenseId) return undefined;
  return licenseById.get(model.licenseId);
}

/**
 * Get family for an ecosystem
 */
export function getEcosystemFamily(ecosystemId: number): BaseModelFamilyRecord | undefined {
  const ecosystem = ecosystemById.get(ecosystemId);
  if (!ecosystem?.familyId) return undefined;
  return ecosystemFamilyById.get(ecosystem.familyId);
}

/**
 * Get all ecosystem defaults (with inheritance from parent ecosystems)
 */
export function getEcosystemDefaults(
  ecosystemId: number
): NonNullable<EcosystemSettings['defaults']> | undefined {
  const settings = ecosystemSettings.find((s) => s.ecosystemId === ecosystemId);
  const ecosystem = ecosystemById.get(ecosystemId);

  // Get parent defaults recursively
  const parentDefaults = ecosystem?.parentEcosystemId
    ? getEcosystemDefaults(ecosystem.parentEcosystemId)
    : undefined;

  // Merge parent defaults with current settings
  if (!settings?.defaults && !parentDefaults) return undefined;

  return {
    ...parentDefaults,
    ...settings?.defaults,
  };
}

// =============================================================================
// Base Model Helpers
// =============================================================================

/**
 * Get active base models for selection UIs (e.g., model version upsert form).
 * Moderators see all base models; regular users see only non-hidden ones.
 */
export function getActiveBaseModels(isModerator?: boolean): BaseModelRecord[] {
  return isModerator ? baseModelRecords : baseModelRecords.filter((m) => !m.hidden);
}

/**
 * Get base model records available for generation (not hidden, not disabled, has generation support)
 */
export function getGenerationBaseModelRecords(): BaseModelRecord[] {
  return baseModelRecords.filter((m) => {
    if (m.hidden || m.disabled) return false;
    return isModelSupported(m.id, 'generation');
  });
}

/**
 * Get base models available for training
 */
export function getTrainingBaseModels(): BaseModelRecord[] {
  return baseModelRecords.filter((m) => {
    if (m.hidden || m.disabled) return false;
    return isModelSupported(m.id, 'training');
  });
}

/**
 * Get disabled base models
 */
export function getDisabledBaseModels(): BaseModelRecord[] {
  return baseModelRecords.filter((m) => m.disabled);
}

/**
 * Get ecosystems for a family
 */
export function getEcosystemsByFamilyId(familyId: number): EcosystemRecord[] {
  return ecosystems.filter((e) => e.familyId === familyId);
}

// =============================================================================
// Ecosystems by Media Type
// =============================================================================

/**
 * Get ecosystem keys that support generation for a given media type.
 * Derived from base models and their ecosystem support.
 */
export function getGenerationEcosystemsForMediaType(mediaType: MediaType): string[] {
  // Get all base models that support generation and match the media type
  const validModels = baseModelRecords.filter((m) => {
    if (m.disabled) return false;
    if (Array.isArray(m.type) ? !m.type.includes(mediaType) : m.type !== mediaType) return false;
    // Check if ecosystem supports generation
    const support = getEcosystemSupport(m.ecosystemId, 'generation');
    return support && !support.disabled;
  });

  // Get unique ecosystem IDs from those models
  const ecosystemIds = new Set(validModels.map((m) => m.ecosystemId));

  // Return ecosystem keys sorted by sortOrder
  return ecosystems
    .filter((e) => ecosystemIds.has(e.id))
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
    .map((e) => e.key);
}

/**
 * Get the default ecosystem key for a media type.
 * Returns the first ecosystem (by sortOrder) that supports generation for this media type.
 */
export function getDefaultEcosystemForMediaType(mediaType: MediaType): string | undefined {
  const ecosystemKeys = getGenerationEcosystemsForMediaType(mediaType);
  return ecosystemKeys[0];
}

/**
 * Get base models available for auction
 */
export function getAuctionBaseModels(): BaseModelRecord[] {
  return baseModelRecords.filter((m) => {
    if (m.hidden || m.disabled) return false;
    return isModelSupported(m.id, 'auction');
  });
}

/**
 * Get compatible base models for an ecosystem and model type
 * Returns base models that can be used with the given ecosystem, split by support level
 */
export function getCompatibleBaseModels(
  ecosystemId: number,
  modelType: ModelType
): { full: BaseModelRecord[]; partial: BaseModelRecord[] } {
  const full: BaseModelRecord[] = [];
  const partial: BaseModelRecord[] = [];

  for (const model of baseModelRecords) {
    if (model.disabled) continue;

    const modelEcosystem = ecosystemById.get(model.ecosystemId);
    if (!modelEcosystem) continue;

    // Check if this model's ecosystem supports the model type for generation
    const support = getEcosystemSupport(model.ecosystemId, 'generation');
    if (!support || support.disabled || !support.modelTypes.includes(modelType)) continue;

    // Get support level
    const supportLevel = getGenerationSupport(ecosystemId, model.ecosystemId, modelType);

    if (supportLevel === 'full') {
      full.push(model);
    } else if (supportLevel === 'partial') {
      partial.push(model);
    }
  }

  return { full, partial };
}

// =============================================================================
// Ecosystem Groups
// =============================================================================

/**
 * Ecosystem group for UI presentation.
 * Groups related ecosystem variants under a single selectable item in BaseModelInput.
 */
export type EcosystemGroup = {
  /** Unique identifier for the group (used as storage key) */
  id: string;
  /** Display name shown in UI */
  displayName: string;
  /** Ecosystem IDs that belong to this group */
  ecosystemIds: number[];
  /** Default ecosystem to use when group is selected */
  defaultEcosystemId: number;
  /** Sort order for display (lower = higher priority) */
  sortOrder: number;
};

/**
 * Ecosystem groups for UI presentation.
 * These group related ecosystem variants (e.g., WanVideo 2.1, 2.2, 2.5) under a single
 * selectable item in the generation form's BaseModelInput.
 */
export const ecosystemGroups: EcosystemGroup[] = [
  {
    id: 'WanVideo',
    displayName: 'Wan Video',
    ecosystemIds: [
      ECO.WanVideo,
      ECO.WanVideo1_3B_T2V,
      ECO.WanVideo14B_T2V,
      ECO.WanVideo14B_I2V_480p,
      ECO.WanVideo14B_I2V_720p,
      ECO.WanVideo22_TI2V_5B,
      ECO.WanVideo22_I2V_A14B,
      ECO.WanVideo22_T2V_A14B,
      ECO.WanVideo25_T2V,
      ECO.WanVideo25_I2V,
      ECO.WanVideo27,
    ],
    defaultEcosystemId: ECO.WanVideo25_T2V,
    sortOrder: 50,
  },
  {
    id: 'WanImage',
    displayName: 'Wan Image',
    ecosystemIds: [ECO.WanImage27],
    defaultEcosystemId: ECO.WanImage27,
    sortOrder: 51,
  },
  {
    id: 'Flux2Klein',
    displayName: 'Flux.2 Klein',
    ecosystemIds: [
      ECO.Flux2Klein_9B,
      ECO.Flux2Klein_9B_base,
      ECO.Flux2Klein_4B,
      ECO.Flux2Klein_4B_base,
    ],
    defaultEcosystemId: ECO.Flux2Klein_9B,
    sortOrder: 22,
  },
  {
    id: 'LTXV',
    displayName: 'LTX Video',
    ecosystemIds: [ECO.LTXV, ECO.LTXV2, ECO.LTXV23, ECO.LTXV25],
    defaultEcosystemId: ECO.LTXV23,
    sortOrder: 205,
  },
  {
    id: 'Seedance',
    displayName: 'Seedance',
    ecosystemIds: [ECO.Seedance],
    defaultEcosystemId: ECO.Seedance,
    sortOrder: 215,
  },

  {
    id: 'ZImage',
    displayName: 'ZImage',
    ecosystemIds: [ECO.ZImageTurbo, ECO.ZImageBase],
    defaultEcosystemId: ECO.ZImageTurbo,
    sortOrder: 100,
  },
];

/**
 * Get the ecosystem group for a given ecosystem ID.
 * Returns undefined if the ecosystem is not part of any group.
 */
export function getEcosystemGroup(ecosystemId: number): EcosystemGroup | undefined {
  return ecosystemGroups.find((g) => g.ecosystemIds.includes(ecosystemId));
}

/**
 * Get the ecosystem group for a given ecosystem key.
 * Returns undefined if the ecosystem is not part of any group.
 */
export function getEcosystemGroupByKey(ecosystemKey: string): EcosystemGroup | undefined {
  const eco = ecosystemByKey.get(ecosystemKey);
  if (!eco) return undefined;
  return getEcosystemGroup(eco.id);
}

/**
 * Get ecosystems that are not part of any group.
 * These are "standalone" ecosystems that should be displayed individually.
 */
export function getStandaloneEcosystems(): EcosystemRecord[] {
  const groupedEcosystemIds = new Set(ecosystemGroups.flatMap((g) => g.ecosystemIds));
  return ecosystems.filter((e) => !groupedEcosystemIds.has(e.id));
}

/**
 * Get storage key for an ecosystem.
 * If the ecosystem is part of a group, returns the group ID.
 * Otherwise, returns the ecosystem key.
 */
export function getEcosystemStorageKey(ecosystem: string): string {
  const eco = ecosystemByKey.get(ecosystem);
  if (!eco) return ecosystem;

  const group = getEcosystemGroup(eco.id);
  return group ? group.id : ecosystem;
}

// =============================================================================
// Display Items (for UI components)
// =============================================================================

/**
 * Display item for UI - represents either an ecosystem group or standalone ecosystem
 */
export type EcosystemDisplayItem = {
  key: string; // Group ID or ecosystem key
  name: string;
  description?: string;
  familyId?: number;
  compatible: boolean;
  type: 'group' | 'ecosystem';
  ecosystemIds?: number[]; // For groups only
  defaultEcosystemId?: number; // For groups only
};

export interface GetEcosystemDisplayItemsOptions {
  /** Ecosystem keys that are compatible (for prioritization) */
  compatibleEcosystems?: string[];
  /** Function to check if an ecosystem is compatible */
  isCompatible?: (ecosystemKey: string) => boolean;
  /** Filter by output type (image/video/audio/model3d) */
  outputType?: 'image' | 'video' | 'audio' | 'model3d';
}

/**
 * Get display items for UI - combines ecosystem groups and standalone ecosystems
 * Handles filtering by output type, compatibility checking, and sorting
 */
export function getEcosystemDisplayItems(
  options: GetEcosystemDisplayItemsOptions = {}
): EcosystemDisplayItem[] {
  const { compatibleEcosystems, isCompatible, outputType } = options;

  // Build set of supported ecosystems
  const supportedEcosystems = compatibleEcosystems ? new Set(compatibleEcosystems) : null;

  // Get ecosystems valid for the current output type.
  //
  // `model3d` is intentionally split out: the Prisma `MediaType` enum only has
  // image/video/audio variants (changing it would require a DB migration), and
  // PolyGen's `BaseModelRecord` is registered with `type: 'image'` to satisfy
  // that enum. We instead enumerate model3d ecosystems explicitly from the
  // ecosystem registry — any ecosystem with a `model3d`-shaped key counts.
  const outputTypeEcosystems = !outputType
    ? null
    : outputType === 'model3d'
    ? new Set(ecosystems.filter((e) => MODEL3D_ECOSYSTEM_IDS.has(e.id)).map((e) => e.key))
    : new Set(getGenerationEcosystemsForMediaType(outputType));

  const groupedEcosystemIds = new Set(ecosystemGroups.flatMap((g) => g.ecosystemIds));
  const result: EcosystemDisplayItem[] = [];

  // Add ecosystem groups
  for (const group of ecosystemGroups) {
    // Check if any ecosystem in group matches output type
    const hasMatchingOutputType = group.ecosystemIds.some((id) => {
      const eco = ecosystemById.get(id);
      if (!eco) return false;
      if (outputTypeEcosystems && !outputTypeEcosystems.has(eco.key)) return false;
      return true;
    });

    if (!hasMatchingOutputType) continue;

    // Get default ecosystem for this group
    const defaultEco = ecosystemById.get(group.defaultEcosystemId);
    if (!defaultEco) continue;

    // Get family from default ecosystem
    const family = defaultEco.familyId ? ecosystemFamilyById.get(defaultEco.familyId) : undefined;

    // Check compatibility - use isCompatible prop if provided, otherwise fall back to supportedEcosystems
    const compatible = isCompatible
      ? isCompatible(defaultEco.key)
      : !supportedEcosystems || supportedEcosystems.has(defaultEco.key);

    result.push({
      type: 'group',
      key: group.id,
      name: group.displayName,
      description: defaultEco.description ?? family?.description,
      familyId: defaultEco.familyId,
      ecosystemIds: group.ecosystemIds,
      defaultEcosystemId: group.defaultEcosystemId,
      compatible,
    });
  }

  // Add standalone ecosystems (not in any group)
  for (const eco of ecosystems) {
    // Skip if in a group
    if (groupedEcosystemIds.has(eco.id)) continue;

    // Filter by output type if specified
    if (outputTypeEcosystems && !outputTypeEcosystems.has(eco.key)) continue;

    // Get description from ecosystem's family
    const family = eco.familyId ? ecosystemFamilyById.get(eco.familyId) : undefined;

    // Check compatibility
    const compatible = isCompatible
      ? isCompatible(eco.key)
      : !supportedEcosystems || supportedEcosystems.has(eco.key);

    result.push({
      type: 'ecosystem',
      key: eco.key,
      name: eco.displayName,
      description: eco.description ?? family?.description,
      familyId: eco.familyId,
      compatible,
    });
  }

  // Sort: compatible first, then by sortOrder
  return result.sort((a, b) => {
    if (a.compatible !== b.compatible) return a.compatible ? -1 : 1;

    // Get sortOrder from group or ecosystem
    let sortA = 999;
    let sortB = 999;

    if (a.type === 'group') {
      const group = ecosystemGroups.find((g) => g.id === a.key);
      sortA = group?.sortOrder ?? 999;
    } else {
      const eco = ecosystemByKey.get(a.key);
      sortA = eco?.sortOrder ?? 999;
    }

    if (b.type === 'group') {
      const group = ecosystemGroups.find((g) => g.id === b.key);
      sortB = group?.sortOrder ?? 999;
    } else {
      const eco = ecosystemByKey.get(b.key);
      sortB = eco?.sortOrder ?? 999;
    }

    return sortA - sortB;
  });
}

// =============================================================================
// Generation Compatibility Check
// =============================================================================

/**
 * Result for a single base model's compatibility check
 */
export type BaseModelCompatibilityStatus = {
  baseModel: string;
  support: SupportLevel | null; // null means not compatible
};

/**
 * An ecosystem that supports one or more of the requested base models
 */
export type CompatibleEcosystemResult = {
  ecosystemId: number;
  ecosystemKey: string;
  baseModels: BaseModelCompatibilityStatus[];
};

/**
 * Result of checking base model compatibility for generation
 */
export type GenerationCompatibilityResult = {
  /** The primary base model used to determine the generation ecosystem */
  primaryBaseModel: string;
  /** The ecosystem determined by the primary base model */
  primaryEcosystem: { id: number; key: string } | null;
  /** Whether all requested base models are compatible with the primary ecosystem */
  allCompatible: boolean;
  /** Compatibility status for each requested base model within the primary ecosystem */
  compatibility: BaseModelCompatibilityStatus[];
  /** List of incompatible base models (those with null support) */
  incompatible: string[];
  /** Alternative ecosystems that support one or more of the incompatible base models */
  alternativeEcosystems: CompatibleEcosystemResult[];
};

/**
 * Check generation compatibility for a set of base models against a primary base model or ecosystem.
 *
 * Given a primary base model name or ecosystem key (e.g., the checkpoint being used for generation)
 * and a list of additional base model names (e.g., LoRAs, embeddings), this function determines:
 * - Which base models are compatible with the primary model's ecosystem
 * - Which are incompatible
 * - What alternative ecosystems could support the incompatible base models
 *
 * @param baseModelOrEcosystem - The name of a base model (e.g., "SDXL 1.0") or ecosystem key (e.g., "SDXL")
 * @param baseModelNames - Array of base model names to check compatibility for
 * @returns Compatibility result with status for each base model and alternative ecosystems
 *
 * @example
 * ```ts
 * // Using a base model name
 * const result = checkGenerationCompatibility('SDXL 1.0', ['Pony', 'SD 1.5', 'Flux.1 D']);
 *
 * // Using an ecosystem key
 * const result = checkGenerationCompatibility('SDXL', ['Pony', 'SD 1.5', 'Flux.1 D']);
 *
 * // result.allCompatible = false
 * // result.compatibility = [
 * //   { baseModel: 'Pony', support: 'partial' },
 * //   { baseModel: 'SD 1.5', support: null },
 * //   { baseModel: 'Flux.1 D', support: null }
 * // ]
 * // result.incompatible = ['SD 1.5', 'Flux.1 D']
 * // result.alternativeEcosystems = [ecosystems that support SD 1.5 or Flux.1 D]
 * ```
 */
export function checkGenerationCompatibility(
  baseModelOrEcosystem: string,
  baseModelNames: string[]
): GenerationCompatibilityResult {
  // Try to find as a base model first, then as an ecosystem
  const primaryAsBaseModel = baseModelByName.get(baseModelOrEcosystem);
  const primaryAsEcosystem = ecosystemByKey.get(baseModelOrEcosystem);

  let primaryEcosystem: EcosystemRecord | undefined;

  if (primaryAsBaseModel) {
    // Found as a base model, get its ecosystem
    primaryEcosystem = ecosystemById.get(primaryAsBaseModel.ecosystemId);
  } else if (primaryAsEcosystem) {
    // Found as an ecosystem directly
    primaryEcosystem = primaryAsEcosystem;
  }

  if (!primaryEcosystem) {
    return {
      primaryBaseModel: baseModelOrEcosystem,
      primaryEcosystem: null,
      allCompatible: false,
      compatibility: baseModelNames.map((name) => ({ baseModel: name, support: null })),
      incompatible: baseModelNames,
      alternativeEcosystems: [],
    };
  }

  // Check each base model's compatibility
  const compatibility: BaseModelCompatibilityStatus[] = baseModelNames.map((name) => {
    const bm = baseModelByName.get(name);
    if (!bm) {
      return { baseModel: name, support: null };
    }

    // Get support level for this base model in the primary ecosystem
    // We need to check what model type this is - assume LORA for addons
    const support = getGenerationSupport(primaryEcosystem!.id, bm.ecosystemId, ModelType.LORA);
    return {
      baseModel: name,
      support,
    };
  });

  // Find incompatible base models
  const incompatible = compatibility.filter((c) => c.support === null).map((c) => c.baseModel);

  // Find alternative ecosystems for incompatible base models
  const alternativeEcosystems: CompatibleEcosystemResult[] = [];

  if (incompatible.length > 0) {
    // Get all ecosystems that have generation support
    const ecosystemsWithSupport = new Set(
      ecosystemSupport
        .filter((s) => s.supportType === 'generation' && !s.disabled)
        .map((s) => s.ecosystemId)
    );

    for (const ecoId of ecosystemsWithSupport) {
      // Skip the primary ecosystem
      if (ecoId === primaryEcosystem.id) continue;

      const eco = ecosystemById.get(ecoId);
      if (!eco) continue;

      // Check which incompatible base models this ecosystem supports
      const ecoBaseModels: BaseModelCompatibilityStatus[] = [];

      for (const bmName of incompatible) {
        const bm = baseModelByName.get(bmName);
        if (!bm) continue;

        const support = getGenerationSupport(ecoId, bm.ecosystemId, ModelType.LORA);
        if (support) {
          ecoBaseModels.push({
            baseModel: bmName,
            support,
          });
        }
      }

      // Only include ecosystems that support at least one incompatible base model
      if (ecoBaseModels.length > 0) {
        alternativeEcosystems.push({
          ecosystemId: eco.id,
          ecosystemKey: eco.key,
          baseModels: ecoBaseModels,
        });
      }
    }

    // Sort alternative ecosystems by number of supported base models (descending)
    alternativeEcosystems.sort((a, b) => b.baseModels.length - a.baseModels.length);
  }

  return {
    primaryBaseModel: baseModelOrEcosystem,
    primaryEcosystem: {
      id: primaryEcosystem.id,
      key: primaryEcosystem.key,
    },
    allCompatible: incompatible.length === 0,
    compatibility,
    incompatible,
    alternativeEcosystems,
  };
}

// =============================================================================
// Compatibility Layer for base-model.constants.ts Migration
// =============================================================================
//
// This section provides backward-compatible exports matching the old base-model.constants.ts API.
// All functions work with string keys/names (not numeric IDs), keeping IDs as internal implementation detail.

// -----------------------------------------------------------------------------
// Type Exports (String-based for backward compatibility)
// -----------------------------------------------------------------------------

/**
 * Base model name (e.g., "SDXL 1.0", "Flux.1 D")
 * @deprecated Use string directly. This type alias exists for backward compatibility.
 */
export type BaseModel = string;

/**
 * Ecosystem key (e.g., "SDXL", "Flux1", "SD1")
 * @deprecated Use string directly. This type alias exists for backward compatibility.
 */
export type BaseModelGroup = string;

// -----------------------------------------------------------------------------
// Array Exports (All return strings, not IDs)
// -----------------------------------------------------------------------------

/**
 * Array of all base model names (including hidden)
 */
export const baseModels: string[] = baseModelRecords.map((x) => x.name);

/**
 * Array of all ecosystem keys
 */
export const baseModelGroups: string[] = [...new Set(ecosystems.map((x) => x.key))];

/**
 * Array of active (non-hidden, non-disabled) base model names
 */
export const activeBaseModels: string[] = getActiveBaseModels().map((x) => x.name);

/**
 * Default base model for brand-new models/versions that have no prior context
 * (no previous version, no remembered last-used selection).
 */
export const defaultBaseModel: BaseModel = 'Anima';

// -----------------------------------------------------------------------------
// Constant Exports
// -----------------------------------------------------------------------------

/**
 * Base models that are deprecated and should not be published or updated
 * Derived from baseModelRecords with disabled=true
 */
export const DEPRECATED_BASE_MODELS: string[] = baseModelRecords
  .filter((x) => x.disabled)
  .map((x) => x.name);

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

/**
 * Get base model configuration by name or ecosystem key
 * @param baseModel - Base model name or ecosystem key
 * @returns Base model record or fallback to "Other"
 */
export function getBaseModelConfig(baseModel: string): BaseModelRecord {
  // Try to find by name first
  const record = baseModelByName.get(baseModel);
  if (record) return record;

  // Try to find by ecosystem key
  const ecosystem = ecosystemByKey.get(baseModel);
  if (ecosystem) {
    // Return first model in this ecosystem
    const models = getBaseModelsByEcosystemId(ecosystem.id, false);
    if (models.length > 0) return models[0];
  }

  // Fallback to "Other"
  return baseModelByName.get('Other') ?? baseModelRecords[0];
}

/**
 * Get ecosystem key for a base model
 * @param baseModel - Base model name OR ecosystem key (e.g., "Nano Banana" or "NanoBanana")
 * @returns Ecosystem key (e.g., "SDXL", "Flux1")
 */
export function getBaseModelGroup(baseModel: string): string {
  // Try to find by base model name first (e.g., "Nano Banana", "Flux.1 D")
  const record = baseModelByName.get(baseModel);
  if (record) {
    const ecosystem = ecosystemById.get(record.ecosystemId);
    return ecosystem?.key ?? 'Other';
  }
  // Fall back to matching by ecosystem key directly (e.g., "NanoBanana", "OpenAI").
  // Matches the legacy base-model.constants.ts behavior where getBaseModelConfig
  // checked both `x.name === baseModel || x.group === baseModel`. Some constants
  // (e.g. generationConfig.NanoBanana.checkpoint.baseModel = 'NanoBanana') store
  // the ecosystem key in the resource's baseModel field instead of the display
  // name, and downstream code like the form-watch subscription in
  // GenerationFormProvider relies on this lookup returning a real ecosystem key
  // instead of collapsing to 'Other'.
  const ecosystem = ecosystemByKey.get(baseModel);
  if (ecosystem) return ecosystem.key;
  return 'Other';
}

/**
 * Get SEO-friendly display name for a base model
 * @param baseModel - Base model name
 * @returns SEO-friendly display name
 */
export function getBaseModelSeoName(baseModel?: string): string {
  if (!baseModel) return 'Stable Diffusion';

  const record = baseModelByName.get(baseModel);
  if (!record) return 'Other';

  const ecosystem = ecosystemById.get(record.ecosystemId);
  if (!ecosystem) return 'Other';

  // If the ecosystem belongs to an ecosystem group, use the group's displayName
  const group = getEcosystemGroup(record.ecosystemId);
  if (group) return group.displayName;

  return ecosystem.displayName ?? ecosystem.key;
}

/**
 * Get media type for a base model
 * @param baseModel - Base model name or ecosystem key
 * @returns Media type ('image' or 'video')
 */
export function getBaseModelMediaType(baseModel: string): MediaType | undefined {
  // Accept both display name and ecosystem key via the fallback chain.
  const record = getBaseModelConfig(baseModel);
  if (!record || record.name === 'Other') return undefined;
  return Array.isArray(record.type) ? record.type[0] : record.type;
}

/**
 * Get engine identifier for a base model
 * @param baseModel - Base model name or ecosystem key
 * @returns Engine string (e.g., "wan", "hunyuan", "veo3") or undefined for models without an engine
 */
export function getBaseModelEngine(baseModel: string): string | undefined {
  // Accept both display name and ecosystem key via the fallback chain.
  const record = getBaseModelConfig(baseModel);
  if (!record || record.name === 'Other') return undefined;
  return getEcosystemSetting(record.ecosystemId, 'engine');
}

/**
 * Get all base model names for an ecosystem
 * @param group - Ecosystem key
 * @returns Array of base model names
 */
export function getBaseModelsByGroup(group: string): string[] {
  const ecosystem = ecosystemByKey.get(group);
  if (!ecosystem) return [];
  return getBaseModelsByEcosystemId(ecosystem.id, false).map((x) => x.name);
}

// -----------------------------------------------------------------------------
// Generation Support Functions
// -----------------------------------------------------------------------------

type BaseModelSupport = { modelTypes: ModelType[]; baseModels: string[] };
type BaseModelGenerationConfig = {
  group: string;
  support: BaseModelSupport[];
  partialSupport?: BaseModelSupport[];
};

type BaseModelSupportType = 'full' | 'partial';
type BaseModelSupportMapped = { baseModel: string; support: BaseModelSupportType };

/**
 * Get generation support configuration for all ecosystems.
 * This is a lazy-computed cache of ecosystem support mapped to the old format.
 */
export const getBaseModelGenerationConfig = lazy(() =>
  ecosystems.map((ecosystem) => {
    const ecosystemModels = getBaseModelsByEcosystemId(ecosystem.id, false);
    const supportMap = new Map<ModelType, BaseModelSupportMapped[]>();

    // Build support map from ecosystem support configuration
    const genSupport = getEcosystemSupport(ecosystem.id, 'generation');
    if (genSupport && !genSupport.disabled) {
      for (const modelType of genSupport.modelTypes) {
        const supported: BaseModelSupportMapped[] = [];

        // Add full support for models in this ecosystem.
        // Hidden models (e.g. NanoBanana, OpenAI, Imagen4 — synthetic API-backed
        // checkpoints) MUST still appear here, otherwise the form's
        // ResourceSelect treats their checkpoint as an unsupported type and
        // nulls the model field on mount. `hidden` only affects whether a base
        // model shows up in user-facing pickers, not whether it supports
        // generation.
        for (const model of ecosystemModels) {
          if (!model.disabled) {
            supported.push({ baseModel: model.name, support: 'full' });
          }
        }

        // Check for cross-ecosystem partial support
        for (const otherEco of ecosystems) {
          if (otherEco.id === ecosystem.id) continue;

          const otherModels = getBaseModelsByEcosystemId(otherEco.id, false);
          for (const otherModel of otherModels) {
            if (otherModel.hidden || otherModel.disabled) continue;

            const crossSupport = getGenerationSupport(ecosystem.id, otherEco.id, modelType);
            if (crossSupport === 'partial') {
              supported.push({ baseModel: otherModel.name, support: 'partial' });
            }
          }
        }

        if (supported.length > 0) {
          supportMap.set(modelType, supported);
        }
      }
    }

    return { group: ecosystem.key, supportMap };
  })
);

/**
 * Get generation configuration for an ecosystem (by key or base model name)
 * @param baseModel - Ecosystem key or base model name
 * @param missedMatch - Internal flag for recursion
 * @returns Generation config or undefined
 */
export function getGenerationBaseModelGroup(
  baseModel: string,
  missedMatch?: boolean
): { group: string; supportMap: Map<ModelType, BaseModelSupportMapped[]> } | undefined {
  const group = getBaseModelGenerationConfig().find((x) => x.group === baseModel);
  if (!group && !missedMatch) {
    // Try to find by base model name
    const record = baseModelByName.get(baseModel);
    if (record) {
      const ecosystem = ecosystemById.get(record.ecosystemId);
      if (ecosystem) return getGenerationBaseModelGroup(ecosystem.key, true);
    }
  }
  return group;
}

/**
 * Resource options for generation (per model type)
 */
export type GenerationBaseModelResourceOptions = {
  type: ModelType;
  baseModels: string[];
  partialSupport: string[];
};

/**
 * Get generation resource options for an ecosystem
 * @param groupName - Ecosystem key
 * @returns Array of resource options per model type
 */
export function getGenerationBaseModelResourceOptions(
  groupName: string
): GenerationBaseModelResourceOptions[] {
  const group = getGenerationBaseModelGroup(groupName);
  if (!group) return [];

  return [...group.supportMap.entries()].map(([modelType, mapped]) => ({
    type: modelType,
    baseModels: [...new Set(mapped.filter((x) => x.support === 'full').map((x) => x.baseModel))],
    partialSupport: [
      ...new Set(mapped.filter((x) => x.support === 'partial').map((x) => x.baseModel)),
    ],
  }));
}

/**
 * Get generation-compatible base models for an ecosystem and model type
 * @param group - Ecosystem key
 * @param modelType - Model type (e.g., LORA, Checkpoint)
 * @returns Array of compatible base model names
 */
export function getGenerationBaseModels(group: string, modelType: ModelType): string[] {
  const match = getGenerationBaseModelGroup(group);
  return match?.supportMap.get(modelType)?.map((x) => x.baseModel) ?? [];
}

/**
 * Get associated ecosystem keys for generation
 * @param group - Ecosystem key
 * @param modelType - Model type
 * @returns Array of associated ecosystem keys
 */
export function getGenerationBaseModelAssociatedGroups(
  group: string,
  modelType: ModelType
): string[] {
  const baseModelNames = getGenerationBaseModels(group, modelType);
  return [
    ...new Set(
      baseModelNames
        .map((name) => {
          const record = baseModelByName.get(name);
          if (!record) return undefined;
          const ecosystem = ecosystemById.get(record.ecosystemId);
          return ecosystem?.key;
        })
        .filter(isDefined)
    ),
  ];
}

/**
 * Check if a base model can be used for auction generation
 * @param baseModel - Base model name
 * @returns True if auction is supported
 */
export function getCanAuctionForGeneration(baseModel?: string): boolean {
  if (!baseModel) return false;
  const record = baseModelByName.get(baseModel);
  if (!record) return false;

  const ecosystem = ecosystemById.get(record.ecosystemId);
  if (!ecosystem) return false;

  // modelLocked ecosystems pin generation to a single checkpoint, so checkpoint auctions don't apply
  if (getEcosystemSetting(record.ecosystemId, 'modelLocked')) return false;

  const noAuctionEcosystems = ['Other'];
  return !noAuctionEcosystems.includes(ecosystem.key);
}
